import React, { useState, useEffect } from 'react';
import {
  Smartphone, Plus, Search, Filter, Edit, Trash2, User,
  MapPin, Building, Calendar, DollarSign, AlertCircle,
  CheckCircle, XCircle, Eye, Download, FileText, Truck, UserPlus, UserMinus, X, Printer, Upload, Copy, Clock, QrCode, Camera, Archive, ArchiveRestore
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { apiService } from '../services/api';
import { showGlobalNotification } from '../hooks/useNotifications';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import Modal from './Modal';
import CollapsibleFilters from './CollapsibleFilters';
import { useLanguage } from '../contexts/LanguageContext';
import { formatFieldChanges, NotificationChangeList, FieldChange } from '../utils/notificationDetails';

interface MobilePhone {
  id: number;
  inventory_number: string;
  creation_date?: string;
  exchange_rate?: number;
  agreement_year?: string;
  currency?: string;
  brand: string;
  model: string;
  color?: string;
  storage?: string;
  imei1: string;
  imei2?: string;
  serial_number?: string;
  subscription_number?: string;
  gsm_number?: string;
  location_id?: number;
  location_name?: string;
  company_id?: number;
  company_name?: string;
  supplier_id?: number;
  supplier_name?: string;
  contract_start_date?: string;
  contract_end_date?: string;
  contract_price?: number;
  monthly_payment?: number;
  assigned_user_id?: number;
  assigned_user_name?: string;
  status: 'active' | 'inactive' | 'damaged' | 'lost';
  notes?: string;
  ownership?: 'company' | 'rental' | 'employee';
  created_at: string;
  created_by_name?: string;
  is_archived?: number | boolean;
  archived_at?: string;
  archived_by_name?: string;
  archive_reason?: string;
  archived_gsm_number?: string;
}

// Helper functions for Turkish number format
type ImportSource = 'file' | 'paste';

interface ImportPreviewUpdate {
  row: number;
  id: number;
  inventory_number: string;
  device: string;
  is_archived: boolean;
  changes: FieldChange[];
}

interface ImportPreviewResult {
  toCreate: number;
  unchanged: number;
  failed: number;
  errors: { row: number; message: string }[];
  updates: ImportPreviewUpdate[];
}

const parseLocalizedNumber = (value: string | number | undefined): number | null => {
  if (!value) return null;
  
  // Eğer zaten sayı ise direkt döndür
  if (typeof value === 'number' && !isNaN(value)) {
    return value;
  }
  
  const stringValue = value.toString().trim();
  if (!stringValue) return null;
  
  // Türk formatı: 23.976,00 (binlik ayırıcı: nokta, ondalık ayırıcı: virgül)
  // İngiliz formatı: 23976.00 (binlik ayırıcı: virgül, ondalık ayırıcı: nokta)
  
  let cleaned = stringValue;
  
  // Virgül varsa (Türk formatı - virgül ondalık ayırıcıdır)
  if (cleaned.includes(',')) {
    const parts = cleaned.split(',');
    const integerPart = parts[0].replace(/\./g, '');
    const decimalPart = parts[1] || '';
    
    // Eğer virgülden sonra 3 karakter varsa ve virgülden önce 2-3 karakter varsa,
    // bu muhtemelen yanlış yazılmış bir binlik ayırıcıdır (kullanıcı nokta yerine virgül kullanmış)
    // Örnek: "23,976" -> "23976" olmalı, "23.976" olmamalı
    if (decimalPart.length === 3 && integerPart.length <= 3 && !cleaned.includes('.')) {
      // Virgülü kaldır, tüm sayıyı birleştir
      cleaned = integerPart + decimalPart;
    } else {
      // Normal Türk formatı: virgül ondalık ayırıcı
      cleaned = integerPart + '.' + decimalPart;
    }
  } else if (cleaned.includes('.')) {
    // Virgül yok, nokta var
    // Türk formatında nokta genellikle binlik ayırıcıdır
    // Ancak son noktadan sonra 1-2 karakter varsa, bu ondalık ayırıcı olabilir (İngiliz formatı)
    const lastDotIndex = cleaned.lastIndexOf('.');
    const afterDot = cleaned.substring(lastDotIndex + 1);
    const beforeLastDot = cleaned.substring(0, lastDotIndex);
    
    // Eğer noktadan sonra 1-2 karakter varsa ve sadece rakam ise, bu ondalık kısım olabilir
    // Ama Türk formatında genellikle binlik ayırıcı olarak kullanılır
    // Kural: Eğer noktadan önce 3'ten fazla karakter varsa ve noktadan sonra 1-2 karakter varsa, 
    // bu İngiliz formatı olabilir (ondalık ayırıcı)
    if (afterDot.length <= 2 && /^\d+$/.test(afterDot) && beforeLastDot.length > 3) {
      // Son nokta ondalık ayırıcı olabilir (İngiliz formatı)
      const beforePart = beforeLastDot.replace(/\./g, '');
      cleaned = beforePart + '.' + afterDot;
    } else {
      // Tüm noktalar binlik ayırıcı (Türk formatı)
      cleaned = cleaned.replace(/\./g, '');
    }
  }
  
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? null : parsed;
};

const formatLocalizedNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) return '';
  return value.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Excel içe aktarmada alanlar (gösterim sırası), ekranda ve şablonda kullanılan başlık, zorunluluk ve değer açıklaması.
// text: şablonda "Metin" biçimli sütun (Excel baştaki sıfırları ve 15 haneden uzun numaraları bozmasın).
// Tanınan başlıkların tam listesi sunucudan gelir (/api/mobile-phones/import-headers).
const IMPORT_FIELDS: { field: string; label: string; required?: 'always' | 'new'; text?: boolean; hint?: string }[] = [
  { field: 'inventory_number', label: 'Envanter No', required: 'always', text: true, hint: 'Sistemde varsa kayıt güncellenir, yoksa yeni kayıt açılır' },
  { field: 'brand', label: 'Marka', required: 'new' },
  { field: 'model', label: 'Model', required: 'new' },
  { field: 'imei1', label: 'IMEI 1', required: 'new', text: true },
  { field: 'imei2', label: 'IMEI 2', text: true },
  { field: 'serial_number', label: 'Seri No', text: true },
  { field: 'subscription_number', label: 'Abonelik No', text: true },
  { field: 'gsm_number', label: 'GSM No', text: true, hint: 'Örn. 0555 111 22 33 veya +90 555 111 22 33' },
  { field: 'color', label: 'Renk' },
  { field: 'storage', label: 'Depolama', hint: 'Örn. 128GB' },
  { field: 'location', label: 'Lokasyon', hint: 'Sistemdeki lokasyon adıyla aynı yazılmalı' },
  { field: 'company', label: 'Firma', hint: 'Sistemdeki firma adıyla aynı yazılmalı' },
  { field: 'supplier', label: 'Tedarikçi', hint: 'Sistemdeki tedarikçi adıyla aynı yazılmalı' },
  { field: 'assigned_person', label: 'Atanan Kullanıcı', hint: 'Sistemdeki kullanıcının adı soyadı' },
  { field: 'status', label: 'Durum', hint: 'Zimmetli, Boşta, Pasif, Arızalı, Kayıp, Çalıntı, Rezerve, Bakımda (boşsa atanan kullanıcıya göre belirlenir)' },
  { field: 'ownership', label: 'Sahiplik', hint: 'Şirket, Kiralık veya Çalışan' },
  { field: 'creation_date', label: 'Oluşturma Tarihi', hint: 'GG.AA.YYYY' },
  { field: 'contract_start_date', label: 'Sözleşme Başlangıç', hint: 'GG.AA.YYYY' },
  { field: 'contract_end_date', label: 'Sözleşme Bitiş', hint: 'GG.AA.YYYY' },
  { field: 'agreement_year', label: 'Anlaşma Yılı', hint: 'Yıl sayısı, örn. 2' },
  { field: 'currency', label: 'Para Birimi', hint: 'TRY, USD, EUR, GBP (TL, Dolar, Euro, Sterlin de olur)' },
  { field: 'exchange_rate', label: 'Döviz Kuru', hint: 'Sayı, örn. 34,50' },
  { field: 'contract_price', label: 'Sözleşme Fiyatı', hint: 'Sayı' },
  { field: 'monthly_payment', label: 'Aylık Ödeme', hint: 'Sayı' },
  { field: 'notes', label: 'Notlar' }
];

// Sunucudaki normalizeImportHeader ile aynı: büyük/küçük harf, "_", "-", ".", ":" ve fazla boşluk fark etmez
const normalizeImportHeader = (header: string) => String(header)
  .replace(/[İI]/g, 'i')
  .toLocaleLowerCase('tr-TR')
  .replace(/[_\-.:]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const MobilePhonesPage: React.FC = () => {
  const { t, language } = useLanguage();
  const [phones, setPhones] = useState<MobilePhone[]>([]);
  const [filteredPhones, setFilteredPhones] = useState<MobilePhone[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPhone, setEditingPhone] = useState<MobilePhone | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedPhoneForHistory, setSelectedPhoneForHistory] = useState<MobilePhone | null>(null);
  const [phoneHistory, setPhoneHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  useGlobalSearch('mobile-phones', setSearchTerm);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [modelFilter, setModelFilter] = useState<string>('all');
  const [contractExpirationFilter, setContractExpirationFilter] = useState<string>('all');
  const [assignmentFilter, setAssignmentFilter] = useState<string>('all'); // 'all', 'assigned', 'unassigned'
  const [locations, setLocations] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [models, setModels] = useState<any[]>([]);
  const [showQuickAssignModal, setShowQuickAssignModal] = useState(false);
  const [selectedPhoneForAssign, setSelectedPhoneForAssign] = useState<MobilePhone | null>(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number>(0);
  
  // Zimmet formu modal states
  const [showAssignmentFormModal, setShowAssignmentFormModal] = useState(false);
  const [assignmentFormNotes, setAssignmentFormNotes] = useState('');
  const [assignmentEquipment, setAssignmentEquipment] = useState<string[]>([]);
  const [pendingAssignment, setPendingAssignment] = useState<{
    phone: MobilePhone | null;
    userId: string;
    userName: string;
  } | null>(null);

  // Zimmet kaldırma modal states
  const [showUnassignConfirmModal, setShowUnassignConfirmModal] = useState(false);
  const [showReturnFormModal, setShowReturnFormModal] = useState(false);
  const [selectedPhoneForUnassign, setSelectedPhoneForUnassign] = useState<MobilePhone | null>(null);
  const [createReturnForm, setCreateReturnForm] = useState(false);
  const [returnFormNotes, setReturnFormNotes] = useState('');
  const [returnFormPhotos, setReturnFormPhotos] = useState<string[]>([]);
  const [returnEquipment, setReturnEquipment] = useState<string[]>([]);

  const PHONE_EQUIPMENT_LIST = [
    'Kılıf', 'Ekran Koruyucu', 'Şarj Aleti', 'Şarj Kablosu',
    'Kulaklık', 'Adaptör', 'Kullanım Kılavuzu', 'Kutu',
  ];

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [paginatedPhones, setPaginatedPhones] = useState<MobilePhone[]>([]);

  const [formData, setFormData] = useState({
    inventory_number: 'TR-MOB-',
    creation_date: '',
    exchange_rate: '',
    agreement_year: '',
    currency: '',
    brand: '',
    model: '',
    color: '',
    storage: '',
    imei1: '',
    imei2: '',
    serial_number: '',
    subscription_number: '',
    gsm_number: '',
    location_id: '',
    company_id: '',
    supplier_id: '',
    contract_start_date: '',
    contract_end_date: '',
    contract_price: '',
    monthly_payment: '',
    assigned_user_id: '',
    status: 'active',
    notes: '',
    ownership: 'rental'
  });

  const [inventoryAvailable, setInventoryAvailable] = useState<boolean | null>(null);
  const [checkingInventory, setCheckingInventory] = useState(false);
  const [imei1Available, setImei1Available] = useState<boolean | null>(null);
  const [imei2Available, setImei2Available] = useState<boolean | null>(null);
  const [checkingImei1, setCheckingImei1] = useState(false);
  const [checkingImei2, setCheckingImei2] = useState(false);
  const [imei1ConflictDevice, setImei1ConflictDevice] = useState<any>(null);
  const [imei2ConflictDevice, setImei2ConflictDevice] = useState<any>(null);
  const [gsmAvailable, setGsmAvailable] = useState<boolean | null>(null);
  const [checkingGsm, setCheckingGsm] = useState(false);
  const [gsmConflictDevice, setGsmConflictDevice] = useState<any>(null);
  const [userHasPhone, setUserHasPhone] = useState<boolean | null>(null);
  const [checkingUser, setCheckingUser] = useState(false);
  const [userConflictDevice, setUserConflictDevice] = useState<any>(null);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [selectedPhoneForLabel, setSelectedPhoneForLabel] = useState<MobilePhone | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [loadingQrCode, setLoadingQrCode] = useState(false);

  // Toplu etiket state'leri
  const [showBulkLabelModal, setShowBulkLabelModal] = useState(false);
  // Arşiv: arşivlenen telefonlar ana listede görünmez, ayrı bir görünümde listelenir
  const [viewMode, setViewMode] = useState<'active' | 'archived'>('active');
  const [archivedPhones, setArchivedPhones] = useState<MobilePhone[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [archiveDialog, setArchiveDialog] = useState<{ action: 'archive' | 'unarchive'; ids: number[] } | null>(null);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiveProcessing, setArchiveProcessing] = useState(false);
  // Arşivlerken GSM numarası varsayılan olarak telefonda kalır; sadece açıkça seçilirse kaldırılır
  const [archiveReleaseGsm, setArchiveReleaseGsm] = useState(false);
  const [selectedPhones, setSelectedPhones] = useState<number[]>([]);
  const [bulkLabelFilters, setBulkLabelFilters] = useState({
    location: 'all',
    status: 'all',
    brand: 'all'
  });
  const [showBulkLabelPreview, setShowBulkLabelPreview] = useState(false);
  const [filteredPhonesForLabel, setFilteredPhonesForLabel] = useState<MobilePhone[]>([]);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [importHeaderAliases, setImportHeaderAliases] = useState<Record<string, string[]> | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState<any>(null);
  const [importData, setImportData] = useState('');
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [columnMapping, setColumnMapping] = useState<{[key: string]: string}>({});
  const [parseStats, setParseStats] = useState<{totalRows: number, errorRows: number}>({totalRows: 0, errorRows: 0});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // Envanter No'su mevcut kayıtlar için içe aktarma önizlemesi: farklar onaylanmadan güncelleme yapılmaz
  const [importPreview, setImportPreview] = useState<{ source: ImportSource; data: ImportPreviewResult } | null>(null);
  const [selectedUpdateIds, setSelectedUpdateIds] = useState<Set<number>>(new Set());

  // API'den gelen marka ve model verilerini kullan
  const getModelsForBrand = (brandName: string) => {
    return models.filter(model => model.brand_name === brandName);
  };

  const colorOptions = ['Siyah', 'Beyaz', 'Mavi', 'Kırmızı', 'Yeşil', 'Mor', 'Altın', 'Gümüş', 'Rose Gold', 'Gri'];
  const storageOptions = ['64GB', '128GB', '256GB', '512GB', '1TB'];

  useEffect(() => {
    loadData();
    // Lokasyonlar sayfasından /mobile-phones?location=ID ile gelindiğinde lokasyon filtresini uygula
    const locationParam = new URLSearchParams(window.location.search).get('location');
    if (locationParam) setLocationFilter(locationParam);
  }, []);

  // Bildirimden veya Dashboard'daki Son Aktiviteler'den belirli bir telefonu aç:
  // tabloyu o kayda filtrele ve düzenleme penceresini aç
  useEffect(() => {
    const handler = async (e: CustomEvent) => {
      if (e.detail?.page !== 'mobile-phones' || !e.detail?.entityId) return;
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/mobile-phones/${e.detail.entityId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        });
        if (!response.ok) {
          showGlobalNotification.error(t('mobilePhones.notifError'), t('mobilePhones.errRecordNotFound'));
          return;
        }
        const phone: MobilePhone = await response.json();
        setStatusFilter('all');
        setLocationFilter('all');
        setModelFilter('all');
        setContractExpirationFilter('all');
        setAssignmentFilter('all');
        setSearchTerm(phone.inventory_number);
        if (phone.is_archived) {
          // Arşivlenmiş telefon düzenlenmez; arşiv görünümünde geçmişi gösterilir
          setViewMode('archived');
          handleShowHistory(phone);
        } else {
          setViewMode('active');
          openEditModal(phone);
        }
      } catch (err) {
        console.error('[openEntityById mobile-phones]', err);
      }
    };
    window.addEventListener('openEntityById', handler as EventListener);
    return () => window.removeEventListener('openEntityById', handler as EventListener);
  }, []);

  useEffect(() => {
    filterPhones();
  }, [phones, archivedPhones, viewMode, searchTerm, statusFilter, locationFilter, modelFilter, contractExpirationFilter, assignmentFilter]);

  // Görünüm değişince seçim temizlenir (aktif ve arşiv listelerinin seçimleri karışmasın)
  useEffect(() => {
    setSelectedIds([]);
  }, [viewMode]);

  // Toplu etiket modal'ı açıldığında filtrelenmiş listeyi başlat
  useEffect(() => {
    if (showBulkLabelModal) {
      setFilteredPhonesForLabel(phones);
    }
  }, [showBulkLabelModal, phones]);

  // Toplu etiket filtreleri değiştiğinde filtreyi uygula
  useEffect(() => {
    if (showBulkLabelModal) {
      applyBulkLabelFilters();
    }
  }, [bulkLabelFilters, phones, showBulkLabelModal]);

  const exportToExcel = () => {
    const locale = language === 'en' ? 'en-US' : 'tr-TR';
    const data = filteredPhones.map(phone => ({
      [t('mobilePhones.colInventoryNo')]: phone.inventory_number,
      [t('mobilePhones.colBrand')]: phone.brand,
      [t('mobilePhones.colModel')]: phone.model,
      [t('mobilePhones.colColor')]: phone.color || '',
      [t('mobilePhones.colStorage')]: phone.storage || '',
      'IMEI 1': phone.imei1,
      'IMEI 2': phone.imei2 || '',
      'Seri No': phone.serial_number || '',
      [t('mobilePhones.colSubscriptionNo')]: phone.subscription_number || '',
      [t('mobilePhones.colGsmNo')]: phone.gsm_number || '',
      [t('mobilePhones.colLocation')]: phone.location_name || '',
      [t('mobilePhones.colCompany')]: phone.company_name || '',
      [t('mobilePhones.colSupplier')]: phone.supplier_name || '',
      [t('mobilePhones.colAssignedUser')]: phone.assigned_user_name || '',
      [t('mobilePhones.colContractStart')]: phone.contract_start_date ? new Date(phone.contract_start_date).toLocaleDateString(locale) : '',
      [t('mobilePhones.colContractEnd')]: phone.contract_end_date ? new Date(phone.contract_end_date).toLocaleDateString(locale) : '',
      [t('mobilePhones.colContractPrice')]: phone.contract_price || '',
      [t('mobilePhones.colMonthlyPayment')]: phone.monthly_payment || '',
      [t('mobilePhones.colStatus')]: getStatusText(phone.status),
      [t('mobilePhones.colNotes')]: phone.notes || '',
      [t('mobilePhones.colCreatedAt')]: new Date(phone.created_at).toLocaleDateString(locale)
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('mobilePhones.title'));

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array', bookSST: false });
    const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `cep-telefonlari-${new Date().toISOString().split('T')[0]}.xlsx`;
    link.click();

    showGlobalNotification.success(t('mobilePhones.notifSuccess'), `${filteredPhones.length} ${t('mobilePhones.exportedToExcel')}`);
  };

  useEffect(() => {
    if (!showBulkImportModal || importHeaderAliases) return;
    fetch('/api/mobile-phones/import-headers', {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
    })
      .then(res => (res.ok ? res.json() : null))
      .then(data => { if (data?.fields) setImportHeaderAliases(data.fields); })
      .catch(() => { /* yardım listesi gösterilmez; içe aktarma yine çalışır */ });
  }, [showBulkImportModal, importHeaderAliases]);

  // İçe aktarma önizlemesinde satırın mevcut kaydı mı güncelleyeceğini göstermek için (arşivdekiler dahil)
  const existingInventoryNumbers = React.useMemo(
    () => new Set([...phones, ...archivedPhones].map(p => String(p.inventory_number || '').trim())),
    [phones, archivedPhones]
  );

  // Doğru başlıklarla boş şablon. Numara sütunları "Metin" biçiminde: Excel baştaki sıfırları silmesin,
  // uzun abonelik/IMEI numaralarını bilimsel gösterime çevirip bozmasın. İkinci sayfa: alan açıklamaları.
  const downloadImportTemplate = () => {
    const TEMPLATE_ROWS = 1000;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([IMPORT_FIELDS.map(f => f.label)]);
    IMPORT_FIELDS.forEach((f, c) => {
      if (!f.text) return;
      for (let r = 1; r <= TEMPLATE_ROWS; r++) {
        ws[XLSX.utils.encode_cell({ r, c })] = { t: 'z', z: '@' };
      }
    });
    ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: TEMPLATE_ROWS, c: IMPORT_FIELDS.length - 1 } });
    ws['!cols'] = IMPORT_FIELDS.map(f => ({ wch: Math.max(16, f.label.length + 2) }));
    XLSX.utils.book_append_sheet(wb, ws, 'Telefonlar');
    const help = XLSX.utils.aoa_to_sheet([
      ['Alan (başlık)', 'Zorunlu', 'Değer', 'Kabul edilen diğer başlık yazımları'],
      ...IMPORT_FIELDS.map(f => [
        f.label,
        f.required === 'always' ? 'Her satırda' : f.required === 'new' ? 'Yeni kayıtta' : '',
        f.hint || '',
        (importHeaderAliases?.[f.field] || []).join(', ')
      ]),
      [],
      ['Notlar'],
      ['Sütun sırası önemli değil; kullanmadığınız sütunları silebilirsiniz.'],
      ['Envanter No sistemde varsa yeni kayıt açılmaz; dolu hücreler mevcut kayıtla karşılaştırılır, farklar onayınıza sunulur. Boş hücre mevcut değeri silmez.'],
      ['Numara sütunları (Envanter No, IMEI, Seri No, Abonelik No, GSM No) metin biçimindedir; başka bir dosyadan yapıştırırken "Yalnızca değerleri yapıştır" kullanın.']
    ]);
    help['!cols'] = [{ wch: 20 }, { wch: 13 }, { wch: 60 }, { wch: 90 }];
    XLSX.utils.book_append_sheet(wb, help, 'Açıklamalar');
    XLSX.writeFile(wb, 'cep-telefonu-ice-aktarma-sablonu.xlsx');
  };

  // Önce önizleme istenir; Envanter No'su mevcut kayıtlarda fark varsa kullanıcıya onaylatılır,
  // yoksa yeni kayıtlar doğrudan eklenir.
  const sendImportRequest = async (source: ImportSource, preview: boolean, approvedIds: number[] = []) => {
    let response: Response;
    if (source === 'file') {
      const formData = new FormData();
      formData.append('file', uploadedFile as File);
      formData.append('preview', String(preview));
      formData.append('approved_update_ids', JSON.stringify(approvedIds));
      response = await fetch('/api/mobile-phones/bulk-import', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
        body: formData
      });
    } else {
      response = await fetch('/api/mobile-phones/bulk-import-paste', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ phones: parsedData, preview, approved_update_ids: approvedIds })
      });
    }
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || t('mobilePhones.errImportFailed'));
    }
    return data;
  };

  const runImport = async (source: ImportSource, approvedIds: number[]) => {
    setImporting(true);
    try {
      const data = await sendImportRequest(source, false, approvedIds);
      setImportResults(data);
      if (data.successful > 0 || data.updated > 0) {
        showGlobalNotification.success(
          t('mobilePhones.notifSuccess'),
          t('mobilePhones.importSummary', { created: data.successful, updated: data.updated })
        );
        if (source === 'paste') setImportData('');
        loadData();
      }
      if (data.failed > 0) {
        showGlobalNotification.warning(
          t('mobilePhones.notifWarning'),
          `${data.failed} ${t('mobilePhones.importFailedRows')}`
        );
      }
    } catch (error: any) {
      showGlobalNotification.error(t('mobilePhones.notifError'), error.message);
    } finally {
      setImporting(false);
    }
  };

  const startImport = async (source: ImportSource) => {
    setImporting(true);
    setError(null);
    setImportResults(null);
    try {
      const preview: ImportPreviewResult = await sendImportRequest(source, true);
      if (preview.updates.length > 0) {
        setSelectedUpdateIds(new Set(preview.updates.map(u => u.id)));
        setImportPreview({ source, data: preview });
        setImporting(false);
        return;
      }
      if (preview.toCreate === 0) {
        // Eklenecek ya da güncellenecek bir şey yok; sadece sonucu göster
        setImportResults({ ...preview, successful: 0, updated: 0 });
        setImporting(false);
        return;
      }
    } catch (error: any) {
      showGlobalNotification.error(t('mobilePhones.notifError'), error.message);
      setImporting(false);
      return;
    }
    await runImport(source, []);
  };

  const confirmImportPreview = async () => {
    if (!importPreview) return;
    const { source } = importPreview;
    setImportPreview(null);
    await runImport(source, [...selectedUpdateIds]);
  };

  const toggleUpdateSelection = (id: number) => {
    setSelectedUpdateIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBulkImport = async () => {
    if (!uploadedFile) {
      showGlobalNotification.error(t('mobilePhones.notifError'), t('mobilePhones.errSelectFile'));
      return;
    }
    await startImport('file');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];

      if (allowedTypes.includes(file.type)) {
        setUploadedFile(file);
        setImportResults(null);
      } else {
        showGlobalNotification.error(t('mobilePhones.notifError'), t('mobilePhones.errSelectExcelFile'));
        event.target.value = '';
      }
    }
  };

  // Parse import data for copy-paste functionality
  const parseImportData = (data: string) => {
    const lines = data.trim().split('\n');
    const phones: any[] = [];
    const errors: string[] = [];
    const columnMapping: {[key: string]: string} = {};
    let totalRows = 0;
    let errorRows = 0;

    if (lines.length < 2) {
      errors.push('En az 2 satır gerekli (başlık satırı + veri)');
      return { phones, errors, columnMapping, totalRows: 0, errorRows: 0 };
    }

    // Header'ları parse et
    const headers = lines[0].split('\t').map(h => h.trim());
    const originalHeaders = [...headers];

    // Sütun eşleştirme pattern'leri - mobile phones için
    const fieldPatterns: {[key: string]: string[]} = {
      inventory_number: ['inventory_number', 'envanter', 'inventory', 'envanter no', 'envanter numarası', 'inventory number', 'stok no', 'asset tag'],
      creation_date: ['creation_date', 'Creation_date', 'oluşturma', 'creation', 'kayıt tarihi', 'creation date', 'create date', 'tarih', 'date'],
      company: ['company', 'şirket', 'sirket', 'firma', 'kurum', 'organization'],
      exchange_rate: ['exchange_rate', 'Exchange_Rate', 'kur', 'döviz', 'exchange', 'rate', 'döviz kuru', 'exchange rate'],
      agreement_year: ['agreement_year', 'aggreement_year', 'anlaşma yıl', 'anlaşma yılı', 'sözleşme yıl', 'sözleşme yılı', 'contract year', 'year', 'yıl', 'yil'],
      currency: ['currency', 'Currency', 'para birimi', 'döviz', 'money', 'birim'],
      brand: ['brand', 'Brand', 'marka', 'manufacturer', 'üretici', 'uretici', 'firma'],
      model: ['model', 'Model', 'modeli', 'model adı', 'model name', 'model_name', 'model no'],
      imei1: ['imei1', 'IMEI1', 'ime1', 'imei 1', 'birinci imei', 'ilk imei', 'imei_1'],
      imei2: ['imei2', 'IMEI2', 'ime2', 'imei 2', 'ikinci imei', 'second imei', 'imei_2'],
      serial_number: ['serial_number', 'serial number', 'serial no', 'serial', 'seri no', 'seri numarası', 'seri numarasi', 'seri_no'],
      subscription_number: ['subscription_number', 'subscription number', 'subscription no', 'abonelik no', 'abonelik numarası', 'abonelik numarasi', 'abonelik_no', 'abone no', 'abone numarası', 'abone numarasi', 'sözleşme no', 'sözleşme numarası', 'sozlesme no', 'sozlesme numarasi', 'contract no', 'contract number'],
      gsm_number: ['gsm_number', 'Mobile Number', 'mobile number', 'gsm', 'telefon', 'phone', 'mobile', 'cep', 'numara', 'gsm no', 'phone number'],
      notes: ['notes', 'Notes', 'not', 'note', 'açıklama', 'aciklama', 'description', 'detay', 'bilgi'],
      ownership: ['ownership', 'Ownership', 'sahiplik', 'mülkiyet', 'mulkiyet', 'owner', 'sahip'],
      status: ['status', 'durum', 'state', 'hal', 'durumu', 'statü'],
      assigned_person: ['assigned_person', 'assigned_user', 'atanan', 'assigned', 'kullanıcı', 'kullanici', 'user', 'person', 'kişi', 'kisi'],
      contract_start_date: ['contract_start_date', 'agreement_start_date', 'sözleşme başlangıç', 'contract start', 'başlangıç', 'baslangic', 'start date'],
      contract_end_date: ['contract_end_date', 'agreement_expiration_date', 'Agreement_expiration_date', 'sözleşme bitiş', 'contract end', 'bitiş', 'bitis', 'end date'],
      color: ['color', 'renk', 'colour', 'colors'],
      storage: ['storage', 'memory', 'hafıza', 'depolama', 'gb', 'capacity'],
      monthly_payment: ['monthly_payment', 'monthly_price', 'aylık fiyat', 'monthly cost', 'aylık ücret', 'monthly fee'],
      contract_price: ['contract_price', 'total_price', 'toplam fiyat', 'total cost', 'toplam ücret', 'toplam tutar'],
      supplier: ['supplier', 'tedarikçi', 'vendor', 'sağlayıcı'],
      location: ['location', 'lokasyon', 'yer', 'konum', 'şube', 'branch']
    };

    // Önce ekranda listelenen başlıklar kesin eşleşme ile atanır (dosya yüklemeyle aynı liste)
    const aliasFields = new Set<string>();
    if (importHeaderAliases) {
      const lookup = new Map<string, string>();
      Object.entries(importHeaderAliases).forEach(([field, aliases]) => {
        aliases.forEach(alias => {
          const key = normalizeImportHeader(alias);
          if (!lookup.has(key)) lookup.set(key, field);
        });
      });
      originalHeaders.forEach(header => {
        const field = lookup.get(normalizeImportHeader(header));
        if (field && !columnMapping[field]) {
          columnMapping[field] = header;
          aliasFields.add(field);
        }
      });
    }
    const aliasHeaders = Object.values(columnMapping);

    // Akıllı sütun eşleştirme (listede olmayan başlıklar için benzerlik araması)
    Object.keys(fieldPatterns).forEach(field => {
      if (aliasFields.has(field)) return;
      const patterns = fieldPatterns[field];
      let bestMatch = '';
      let bestScore = 0;
      // "Seri Numarası" / "Abonelik Numarası" gibi sütunlar 'numara' kalıbı yüzünden GSM No'yu kapmasın;
      // bu alanlara ayrılmış sütunlar GSM eşleştirmesinde atlanır, böylece "Telefon Numarası" seçilebilir
      const reservedHeaders = field === 'gsm_number'
        ? [columnMapping.serial_number, columnMapping.subscription_number].filter(Boolean)
        : [];

      patterns.forEach(pattern => {
        originalHeaders.forEach((header, index) => {
          if (reservedHeaders.includes(originalHeaders[index]) || aliasHeaders.includes(originalHeaders[index])) return;
          const normalizedHeader = String(header || '').toLowerCase().trim();
          const normalizedPattern = String(pattern || '').toLowerCase().trim();

          // Tam eşleşme
          if (normalizedHeader === normalizedPattern) {
            columnMapping[field] = originalHeaders[index];
            bestScore = 100;
            bestMatch = originalHeaders[index];
          }
          // Header içinde pattern var mı
          else if (normalizedHeader.includes(normalizedPattern)) {
            const score = (normalizedPattern.length / normalizedHeader.length) * 90;
            if (score > bestScore) {
              bestMatch = originalHeaders[index];
              bestScore = score;
            }
          }
          // Pattern içinde header var mı
          else if (normalizedPattern.includes(normalizedHeader)) {
            const score = (normalizedHeader.length / normalizedPattern.length) * 80;
            if (score > bestScore) {
              bestMatch = originalHeaders[index];
              bestScore = score;
            }
          }
          // Kısmi eşleşme
          else if (header.includes(pattern) || pattern.includes(header)) {
            const score = Math.min(header.length, pattern.length) / Math.max(header.length, pattern.length) * 70;
            if (score > bestScore) {
              bestMatch = originalHeaders[index];
              bestScore = score;
            }
          }
        });
      });

      if (bestScore > 30) {
        columnMapping[field] = bestMatch;
      }
    });

    // "Seri Numarası" sütunu 'numara' kalıbı yüzünden GSM No olarak eşleşmesin
    if (columnMapping.serial_number && columnMapping.gsm_number === columnMapping.serial_number) {
      delete columnMapping.gsm_number;
    }
    // "Abonelik Numarası" / "Sözleşme No" sütunu başka alanlarla (GSM No, sözleşme tarihleri vb.) eşleşmesin
    if (columnMapping.subscription_number) {
      Object.keys(columnMapping).forEach(field => {
        if (field !== 'subscription_number' && columnMapping[field] === columnMapping.subscription_number) {
          delete columnMapping[field];
        }
      });
    }


    // Tanınmayan sütunları uyar
    const recognizedColumns = Object.values(columnMapping);
    const unrecognizedColumns = originalHeaders.filter(col => !recognizedColumns.includes(col));
    if (unrecognizedColumns.length > 0) {
      errors.push(`Tanınmayan sütunlar: ${unrecognizedColumns.join(', ')}`);
    }

    // Gerekli sütunları kontrol et. Marka/Model/IMEI1 yalnızca yeni kayıt için gerekir; Envanter No'su
    // mevcut kayıtlar güncellenir, bu yüzden bu alanlar sunucuda kayıt bazında kontrol edilir.
    if (!columnMapping.inventory_number) {
      errors.push('Gerekli sütunlar eksik: Envanter No');
    }

    // Veri parse etme
    totalRows = lines.length - 1; // Başlık satırı hariç
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Boş satırları atla
      if (!line) {
        continue;
      }
      
      const values = line.split('\t');
      
      // Sütun sayısı kontrolü - daha esnek
      if (values.length < Math.max(1, headers.length * 0.8)) { // En az %80 sütun olmalı
        errors.push(`Satır ${i + 1}: Eksik sütun verisi (${values.length}/${headers.length} sütun)`);
        errorRows++;
        continue;
      }
      
      // Eksik sütunları boş string ile doldur
      while (values.length < headers.length) {
        values.push('');
      }

      const phone: any = { rowNumber: i + 1 };
      let hasRequiredFields = true;

      // Sütun eşleştirmesine göre veri al
      Object.keys(columnMapping).forEach(field => {
        const columnName = columnMapping[field];
        const columnIndex = originalHeaders.indexOf(columnName);
        const value = values[columnIndex]?.trim();

        switch (field) {
          case 'inventory_number':
            phone.inventory_number = value;
            if (!value || value.trim() === '') {
              errors.push(`Satır ${i + 1}: Envanter numarası gerekli`);
              hasRequiredFields = false;
            }
            break;
          case 'brand':
            phone.brand = value;
            break;
          case 'model':
            phone.model = value;
            break;
          case 'imei1':
            phone.imei1 = value;
            break;
          case 'creation_date':
            phone.creation_date = value;
            break;
          case 'company':
            phone.company = value;
            break;
          case 'exchange_rate':
            phone.exchange_rate = value ? parseFloat(value) : null;
            break;
          case 'agreement_year':
            phone.agreement_year = value;
            break;
          case 'currency':
            phone.currency = value;
            break;
          case 'imei2':
            phone.imei2 = value;
            break;
          case 'serial_number':
            phone.serial_number = value;
            break;
          case 'subscription_number':
            phone.subscription_number = value;
            break;
          case 'gsm_number':
            phone.gsm_number = value;
            break;
          case 'notes':
            phone.notes = value;
            break;
          case 'ownership':
            phone.ownership = value;
            break;
          case 'status':
            phone.status = value || 'active';
            break;
          case 'supplier':
            phone.supplier = value;
            break;
          case 'location':
            phone.location = value;
            break;
          case 'assigned_person':
            phone.assigned_person = value;
            break;
          case 'contract_start_date':
            phone.contract_start_date = value;
            break;
          case 'contract_end_date':
            phone.contract_end_date = value;
            break;
          case 'color':
            phone.color = value;
            break;
          case 'storage':
            phone.storage = value;
            break;
          case 'monthly_payment':
            phone.monthly_payment = parseLocalizedNumber(value);
            break;
          case 'contract_price':
            phone.contract_price = parseLocalizedNumber(value);
            break;
          default:
            phone[field] = value;
        }
      });

      if (hasRequiredFields) {
        phones.push(phone);
      } else {
        errorRows++;
      }
    }

    // Debug sonuçları

    return { phones, errors, columnMapping, totalRows, errorRows };
  };

  const handleDataChange = (data: string) => {
    setImportData(data);

    if (data.trim()) {
      const result = parseImportData(data);
      setParsedData(result.phones);
      setParseErrors(result.errors);
      setColumnMapping(result.columnMapping);
      setParseStats({totalRows: result.totalRows, errorRows: result.errorRows});
    } else {
      setParsedData([]);
      setParseErrors([]);
      setColumnMapping({});
      setParseStats({totalRows: 0, errorRows: 0});
    }
  };

  const handleCopyPasteImport = async () => {
    if (!importData.trim()) {
      setError(t('mobilePhones.errPasteData'));
      return;
    }

    if (parsedData.length === 0) {
      setError(t('mobilePhones.errNoValidData'));
      return;
    }

    // Kısmi import uyarısı
    if (parseErrors.length > 0) {
      const confirmMessage = language === 'en'
        ? `${parseErrors.length} rows have errors. Import only the valid ${parsedData.length} rows?`
        : `${parseErrors.length} satırda hata var. Sadece geçerli ${parsedData.length} satırı import etmek istiyor musunuz?`;
      if (!confirm(confirmMessage)) {
        return;
      }
    }

    await startImport('paste');
  };

  const resetImportModal = () => {
    setImportData('');
    setImportResults(null);
    setError(null);
    setSuccess(null);
    setParsedData([]);
    setParseErrors([]);
    setColumnMapping({});
    setUploadedFile(null);
    setImportPreview(null);
  };

  // Zimmet formu oluşturma fonksiyonu
  const createAssignmentForm = async (phone: MobilePhone, userId: string, notes: string = '', openInNewTab: boolean = true, equipment: string[] = []) => {
    try {
      const response = await fetch('/api/assignment-forms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          mobile_phone_id: phone.id,
          assigned_user_id: userId,
          notes: notes,
          delivered_equipment: equipment.length > 0 ? equipment : undefined,
        })
      });

      if (!response.ok) {
        throw new Error(t('mobilePhones.errAssignmentFormFailed'));
      }

      const form = await response.json();
      showGlobalNotification.success(t('mobilePhones.notifSuccess'), t('mobilePhones.assignmentFormCreated'));

      // Formu yeni sekmede aç (sadece istenirse)
      if (openInNewTab) {
        window.open(`/assignment-forms/${form.id}`, '_blank');
      }

      return form;
    } catch (error: any) {
      showGlobalNotification.error(t('mobilePhones.notifError'), error.message);
      throw error;
    }
  };

  // Atama işlemi sırasında zimmet formu onayı
  const handleAssignmentWithForm = (phone: MobilePhone, userId: string, userName: string) => {
    setPendingAssignment({ phone, userId, userName });
    setAssignmentFormNotes('');
    setShowAssignmentFormModal(true);
  };

  // Zimmet formu onaylandığında
  const confirmAssignmentWithForm = async () => {
    if (!pendingAssignment) return;

    try {
      // Önce zimmet formu oluştur (yeni sekmede açma)
      const form = await createAssignmentForm(pendingAssignment.phone!, pendingAssignment.userId, assignmentFormNotes, false, assignmentEquipment);

      // Sonra atama işlemini gerçekleştir
      const response = await fetch(`/api/mobile-phones/${pendingAssignment.phone!.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          assigned_user_id: parseInt(pendingAssignment.userId)
        })
      });

      if (response.ok) {
        showGlobalNotification.success(t('mobilePhones.notifSuccess'), t('mobilePhones.phoneAssignedWithForm'));

        // Modal'ı kapat
        setShowAssignmentFormModal(false);
        setPendingAssignment(null);
        setAssignmentFormNotes('');
        setAssignmentEquipment([]);

        // Zimmet formları sayfasına yönlendir
        window.location.href = '/assignment-forms';
      } else {
        const errorData = await response.json();
        showGlobalNotification.error(t('mobilePhones.notifError'), errorData.message || t('mobilePhones.errAssignFailed'));
      }
    } catch (error) {
      console.error('Assignment with form error:', error);
      showGlobalNotification.error(t('mobilePhones.notifError'), t('mobilePhones.errAssignFailed'));
    }
  };

  // Zimmet formu olmadan atama
  const confirmAssignmentWithoutForm = async () => {
    if (!pendingAssignment) return;

    try {
      const response = await fetch(`/api/mobile-phones/${pendingAssignment.phone!.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          assigned_user_id: parseInt(pendingAssignment.userId)
        })
      });

      if (response.ok) {
        showGlobalNotification.success(t('mobilePhones.notifSuccess'), t('mobilePhones.phoneAssigned'));
        loadData();
      } else {
        const errorData = await response.json();
        showGlobalNotification.error(t('mobilePhones.notifError'), errorData.message || t('mobilePhones.errAssignFailed'));
      }

      // Modal'ı kapat
      setShowAssignmentFormModal(false);
      setPendingAssignment(null);
      setAssignmentFormNotes('');
      setAssignmentEquipment([]);
    } catch (error) {
      console.error('Assignment without form error:', error);
      showGlobalNotification.error(t('mobilePhones.notifError'), t('mobilePhones.errAssignFailed'));
    }
  };

  const uniqueModels = [...new Set(phones.map(p => p.model).filter(Boolean))];

  const loadData = async () => {
    try {
      setLoading(true);


      const [phonesData, locationsData, companiesData, allSuppliersData, usersData, brandsData, modelsData] = await Promise.all([
        fetch('/api/mobile-phones', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }).then(res => res.json()),
        apiService.getLocations(),
        apiService.getCompanies(),
        apiService.getSuppliers().then(data => {
          return data;
        }),
        apiService.getUsers(),
        fetch('/api/brands', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }).then(res => res.json()),
        fetch('/api/models', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }).then(res => res.json())
      ]);


      // Tüm tedarikçileri göster (filtre kaldırıldı)
      const suppliersArray = Array.isArray(allSuppliersData) ? allSuppliersData : [];


      setPhones(Array.isArray(phonesData) ? phonesData : []);
      loadArchivedPhones();
      setLocations(Array.isArray(locationsData) ? locationsData : []);
      setCompanies(Array.isArray(companiesData) ? companiesData : []);
      setSuppliers(suppliersArray);
      setUsers(Array.isArray(usersData) ? usersData : []);
      setBrands(Array.isArray(brandsData) ? brandsData : []);
      setModels(Array.isArray(modelsData) ? modelsData : []);

      // Apply initial pagination
      const safePhones = Array.isArray(phonesData) ? phonesData : [];
      setFilteredPhones(safePhones);
      updatePaginatedPhones(safePhones, 1);
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error(t('mobilePhones.notifError'), t('mobilePhones.errLoadData'));
    } finally {
      setLoading(false);
    }
  };

  // Sözleşme bitiş filtresi; "Sözleşme Bitiyor" kartının sayısı da aynı hesaplamayı kullanır
  const matchesContractFilter = (phone: MobilePhone, filter: string) => {
    if (!phone.contract_end_date) return filter === 'no_contract';
    const diffDays = Math.ceil((new Date(phone.contract_end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    switch (filter) {
      case 'expired': return diffDays < 0;
      case 'expires_today': return diffDays === 0;
      case 'expires_week': return diffDays >= 0 && diffDays <= 7;
      case 'expires_month': return diffDays >= 0 && diffDays <= 30;
      case 'expires_3months': return diffDays >= 0 && diffDays <= 90;
      default: return false;
    }
  };

  const loadArchivedPhones = async () => {
    try {
      const response = await fetch('/api/mobile-phones?archived=only', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await response.json();
      setArchivedPhones(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Arşivlenen telefonlar yüklenemedi:', error);
    }
  };

  const toggleSelected = (id: number) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const openArchiveDialog = (action: 'archive' | 'unarchive', ids: number[]) => {
    if (ids.length === 0) return;
    setArchiveReason('');
    setArchiveReleaseGsm(false);
    setArchiveDialog({ action, ids });
  };

  const handleArchiveConfirm = async () => {
    if (!archiveDialog) return;
    const { action, ids } = archiveDialog;
    setArchiveProcessing(true);
    try {
      const response = await fetch(`/api/mobile-phones/${action === 'archive' ? 'bulk-archive' : 'bulk-unarchive'}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(action === 'archive' ? { ids, reason: archiveReason, release_gsm: archiveReleaseGsm } : { ids })
      });
      const result = await response.json();
      if (!response.ok) {
        showGlobalNotification.error(t('mobilePhones.notifError'), result.error || t('mobilePhones.archive.errGeneric'));
        return;
      }
      if (action === 'archive') {
        showGlobalNotification.success(
          t('mobilePhones.notifSuccess'),
          t('mobilePhones.archive.successArchived', { count: result.archived })
            + (result.unassigned > 0 ? ' ' + t('mobilePhones.archive.successUnassigned', { count: result.unassigned }) : '')
            + (result.gsm_released > 0 ? ' ' + t('mobilePhones.archive.successGsmReleased', { count: result.gsm_released }) : '')
        );
      } else {
        showGlobalNotification.success(t('mobilePhones.notifSuccess'), t('mobilePhones.archive.successRestored', { count: result.restored }));
        if (Array.isArray(result.gsm_conflicts) && result.gsm_conflicts.length > 0) {
          showGlobalNotification.warning(
            t('mobilePhones.notifWarning'),
            t('mobilePhones.archive.gsmConflict', {
              list: result.gsm_conflicts.map((c: any) => `${c.inventory_number} (${c.gsm_number} → ${c.used_by})`).join(', ')
            })
          );
        }
      }
      setSelectedIds([]);
      setArchiveDialog(null);
      await loadData();
    } catch (error) {
      console.error('Arşiv işlemi hatası:', error);
      showGlobalNotification.error(t('mobilePhones.notifError'), t('mobilePhones.archive.errGeneric'));
    } finally {
      setArchiveProcessing(false);
    }
  };

  const filterPhones = () => {
    let filtered = viewMode === 'archived' ? archivedPhones : phones;

    if (searchTerm) {
      filtered = filtered.filter(phone =>
        String(phone.inventory_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(phone.brand || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(phone.model || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(phone.imei1 || '').includes(searchTerm) ||
        (phone.imei2 && String(phone.imei2).includes(searchTerm)) ||
        (phone.serial_number && String(phone.serial_number).toLowerCase().includes(searchTerm.toLowerCase())) ||
        (phone.subscription_number && String(phone.subscription_number).toLowerCase().includes(searchTerm.toLowerCase())) ||
        (phone.archived_gsm_number && String(phone.archived_gsm_number).includes(searchTerm)) ||
        (phone.assigned_user_name && String(phone.assigned_user_name).toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(phone => phone.status === statusFilter);
    }

    if (locationFilter !== 'all') {
      filtered = filtered.filter(phone => phone.location_id?.toString() === locationFilter);
    }

    if (modelFilter !== 'all') {
      filtered = filtered.filter(phone => phone.model === modelFilter);
    }

    if (assignmentFilter !== 'all') {
      if (assignmentFilter === 'assigned') {
        filtered = filtered.filter(phone => phone.assigned_user_id);
      } else if (assignmentFilter === 'unassigned') {
        filtered = filtered.filter(phone => !phone.assigned_user_id);
      }
    }

    if (contractExpirationFilter !== 'all') {
      filtered = filtered.filter(phone => matchesContractFilter(phone, contractExpirationFilter));
    }

    setFilteredPhones(filtered);
    
    // Reset to first page when filters change
    setCurrentPage(1);
    
    // Apply pagination
    updatePaginatedPhones(filtered, 1);
  };

  const updatePaginatedPhones = (filteredData: MobilePhone[], page: number) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = filteredData.slice(startIndex, endIndex);
    setPaginatedPhones(paginated);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    updatePaginatedPhones(filteredPhones, page);
  };

  const getTotalPages = () => {
    return Math.ceil(filteredPhones.length / itemsPerPage);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const url = editingPhone ? `/api/mobile-phones/${editingPhone.id}` : '/api/mobile-phones';
      const method = editingPhone ? 'PUT' : 'POST';

      // Para birimi değerlerini parse et (Türk Lirası formatından sayıya çevir)
      // Boş string'leri NULL'a çevir
      const submitData = {
        ...formData,
        gsm_number: formData.gsm_number && formData.gsm_number.trim() !== '' ? formData.gsm_number : null,
        imei2: formData.imei2 && formData.imei2.trim() !== '' ? formData.imei2 : null,
        serial_number: formData.serial_number && formData.serial_number.trim() !== '' ? formData.serial_number.trim() : null,
        subscription_number: formData.subscription_number && formData.subscription_number.trim() !== '' ? formData.subscription_number.trim() : null,
        brand: formData.brand && formData.brand.trim() !== '' ? formData.brand : null,
        model: formData.model && formData.model.trim() !== '' ? formData.model : null,
        color: formData.color && formData.color.trim() !== '' ? formData.color : null,
        storage: formData.storage && formData.storage.trim() !== '' ? formData.storage : null,
        notes: formData.notes && formData.notes.trim() !== '' ? formData.notes : null,
        contract_price: parseLocalizedNumber(formData.contract_price),
        monthly_payment: parseLocalizedNumber(formData.monthly_payment)
      };

      const send = (body: object) => fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(body)
      });

      let response = await send(submitData);
      let data = await response.json();

      // Numara arşivdeki bir telefonda kayıtlıysa kullanıcıya sorulur; onay verilirse oradan aktarılır
      if (response.status === 409 && data.code === 'GSM_ON_ARCHIVED_PHONE') {
        const device = data.device || {};
        const approved = confirm(t('mobilePhones.archive.gsmTransferConfirm', {
          gsm: submitData.gsm_number || '',
          device: `${device.inventory_number} (${device.brand} ${device.model})`
        }));
        if (!approved) return;
        response = await send({ ...submitData, transfer_gsm_from_archived: true });
        data = await response.json();
      }

      if (!response.ok) {
        throw new Error(data.error || t('mobilePhones.errOperationFailed'));
      }

      showGlobalNotification.success(
        t('mobilePhones.notifSuccess'),
        editingPhone ? t('mobilePhones.phoneUpdated') : t('mobilePhones.phoneAdded')
      );

      setShowModal(false);
      resetForm();
      loadData();
    } catch (error: any) {
      showGlobalNotification.error(t('mobilePhones.notifError'), error.message);
    }
  };

  // Contract validation helper function
  const checkContractStatus = (phone: MobilePhone) => {
    if (!phone.contract_end_date) {
      return { isExpired: false, isExpiringSoon: false, daysUntilExpiry: null };
    }

    const today = new Date();
    const contractEndDate = new Date(phone.contract_end_date);
    const timeDiff = contractEndDate.getTime() - today.getTime();
    const daysUntilExpiry = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    return {
      isExpired: daysUntilExpiry < 0,
      isExpiringSoon: daysUntilExpiry >= 0 && daysUntilExpiry <= 90, // 3 months = 90 days
      daysUntilExpiry: daysUntilExpiry
    };
  };

  const handleQuickAssign = (phone: MobilePhone) => {
    const contractStatus = checkContractStatus(phone);

    // Prevent assignment if contract has expired
    if (contractStatus.isExpired) {
      const locale = language === 'en' ? 'en-US' : 'tr-TR';
      showGlobalNotification.error(
        t('mobilePhones.errCannotAssign'),
        `${t('mobilePhones.errContractExpired')} (${new Date(phone.contract_end_date!).toLocaleDateString(locale)}). ${t('mobilePhones.errExpiredCannotAssign')}`
      );
      return;
    }

    setSelectedPhoneForAssign(phone);
    setUserSearchTerm('');
    setSelectedUserId(0);
    setShowQuickAssignModal(true);
  };


  const handleQuickAssignSave = async () => {
    if (!selectedPhoneForAssign || selectedUserId === 0) {
      showGlobalNotification.error(t('mobilePhones.notifError'), t('mobilePhones.errSelectUser'));
      return;
    }

    // Check contract status before assignment
    const contractStatus = checkContractStatus(selectedPhoneForAssign);

    // Warning for contracts expiring within 3 months
    if (contractStatus.isExpiringSoon && contractStatus.daysUntilExpiry !== null) {
      const locale = language === 'en' ? 'en-US' : 'tr-TR';
      const confirmAssign = confirm(
        language === 'en'
          ? `This phone's contract is expiring soon (${contractStatus.daysUntilExpiry} days left - ${new Date(selectedPhoneForAssign.contract_end_date!).toLocaleDateString(locale)}). Do you still want to assign it to a user?`
          : `Bu telefonun sözleşme bitiş tarihi yaklaşıyor (${contractStatus.daysUntilExpiry} gün kaldı - ${new Date(selectedPhoneForAssign.contract_end_date!).toLocaleDateString(locale)}). Yine de kullanıcıya atama yapmak istiyor musunuz?`
      );

      if (!confirmAssign) {
        return;
      }
    }

    if (selectedPhoneForAssign.assigned_user_id) {
      const currentUser = users.find(u => u.id === selectedPhoneForAssign.assigned_user_id);
      const newUser = users.find(u => u.id === selectedUserId);

      if (selectedPhoneForAssign.assigned_user_id === selectedUserId) {
        showGlobalNotification.warning(t('mobilePhones.notifWarning'), t('mobilePhones.errAlreadyAssigned'));
        return;
      }

      const confirmChange = confirm(
        language === 'en'
          ? `This phone is currently assigned to "${currentUser?.name}". Are you sure you want to reassign it to "${newUser?.name}"?`
          : `Bu telefon şu anda "${currentUser?.name}" kullanıcısına atanmış. Atamayı "${newUser?.name}" kullanıcısına değiştirmek istediğinizden emin misiniz?`
      );

      if (!confirmChange) {
        return;
      }
    }

    try {
      const response = await fetch(`/api/mobile-phones/${selectedPhoneForAssign.id}/quick-assign`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          assigned_user_id: selectedUserId
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || t('mobilePhones.errAssignFailed'));
      }

      const assignedUser = users.find(u => u.id === selectedUserId);
      showGlobalNotification.success(
        t('mobilePhones.notifSuccess'),
        `${selectedPhoneForAssign.brand} ${selectedPhoneForAssign.model} "${assignedUser?.name}" ${t('mobilePhones.assignedToUser')}`
      );

      setShowQuickAssignModal(false);
      setSelectedPhoneForAssign(null);
      setSelectedUserId(0);
      loadData();
    } catch (error: any) {
      showGlobalNotification.error(t('mobilePhones.notifError'), error.message);
    }
  };


  // Etiket için QR kod yükle
  const loadQRCodeForLabel = async (phone: MobilePhone) => {
    if (!phone) return;

    setLoadingQrCode(true);
    try {
      const token = localStorage.getItem('auth_token');
      const siteUrl = import.meta.env.VITE_SITE_URL || `${window.location.protocol}//${window.location.host}`;
      const response = await fetch(`${siteUrl}/api/mobile-phones/${phone.id}/qr-code?token=${token}&embed=true`);

      if (response.ok) {
        const data = await response.json();
        setQrCodeUrl(data.qrCode);
      } else {
        console.error('QR kod yüklenemedi');
        setQrCodeUrl(null);
      }
    } catch (error) {
      console.error('QR kod yükleme hatası:', error);
      setQrCodeUrl(null);
    } finally {
      setLoadingQrCode(false);
    }
  };

  // Toplu etiket fonksiyonları
  const applyBulkLabelFilters = () => {
    let filtered = [...phones];

    if (bulkLabelFilters.location !== 'all') {
      filtered = filtered.filter(p => p.location_name === bulkLabelFilters.location);
    }

    if (bulkLabelFilters.status !== 'all') {
      filtered = filtered.filter(p => p.status === bulkLabelFilters.status);
    }

    if (bulkLabelFilters.brand !== 'all') {
      filtered = filtered.filter(p => p.brand === bulkLabelFilters.brand);
    }

    setFilteredPhonesForLabel(filtered);
  };

  const handlePhoneSelection = (phoneId: number) => {
    setSelectedPhones(prev =>
      prev.includes(phoneId)
        ? prev.filter(id => id !== phoneId)
        : [...prev, phoneId]
    );
  };

  const selectAllFiltered = () => {
    const currentList = filteredPhonesForLabel.length > 0 ? filteredPhonesForLabel : phones;
    const allIds = currentList.map(p => p.id);
    setSelectedPhones(allIds);
  };

  const deselectAll = () => {
    setSelectedPhones([]);
  };

  const generateBulkLabelPreview = () => {
    setShowBulkLabelPreview(true);
  };

  const printBulkLabels = async () => {
    const selectedPhoneData = phones.filter(p => selectedPhones.includes(p.id));
    const token = localStorage.getItem('auth_token');

    // Get QR codes for all selected phones
    const qrCodes = {};
    for (const phone of selectedPhoneData) {
      try {
        const siteUrl = import.meta.env.VITE_SITE_URL || `${window.location.protocol}//${window.location.host}`;
      const response = await fetch(`${siteUrl}/api/mobile-phones/${phone.id}/qr-code?token=${token}&embed=true`);
        if (response.ok) {
          const data = await response.json();
          qrCodes[phone.id] = data.qrCode;
        }
      } catch (error) {
        console.error('Error fetching QR code for phone', phone.id, error);
      }
    }

    // Create print-friendly page
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(generateBulkLabelHTML(selectedPhoneData, qrCodes));
    printWindow.document.close();

    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
    }, 1000);
  };

  const generateBulkLabelHTML = (phones: MobilePhone[], qrCodes: {[key: number]: string}) => {
    const labelsPerRow = 3;
    const labelsPerCol = 7;
    const labelsPerPage = labelsPerRow * labelsPerCol;
    const pages = [];

    for (let i = 0; i < phones.length; i += labelsPerPage) {
      const pageLabels = phones.slice(i, i + labelsPerPage);
      pages.push(pageLabels);
    }

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Cep Telefonu Etiketleri</title>
        <style>
          @page {
            size: A4;
            margin: 8mm;
          }
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
          }
          .page {
            display: grid;
            grid-template-columns: repeat(${labelsPerRow}, 1fr);
            gap: 3mm;
            page-break-after: always;
          }
          .page:last-child {
            page-break-after: avoid;
          }
          .label {
            border: 1px solid #ccc;
            border-radius: 4px;
            padding: 3mm 3mm;
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 2mm;
            background: white;
            height: 32mm;
            box-sizing: border-box;
            overflow: hidden;
          }
          .label-text {
            flex: 1;
            min-width: 0;
          }
          .brand-name {
            font-size: 13px;
            font-weight: bold;
            color: #111;
            line-height: 1.2;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .model-name {
            font-size: 11px;
            color: #444;
            line-height: 1.2;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .qr-code {
            width: 28mm;
            height: 28mm;
            flex-shrink: 0;
          }
        </style>
      </head>
      <body>
        ${pages.map(pageLabels => `
          <div class="page">
            ${pageLabels.map(phone => `
              <div class="label">
                <div class="label-text">
                  <div class="brand-name">${phone.brand}</div>
                  <div class="model-name">${phone.model}</div>
                </div>
                ${qrCodes[phone.id] ?
                  `<img class="qr-code" src="${qrCodes[phone.id]}" alt="QR Code" />` :
                  '<div class="qr-code" style="background: #f0f0f0; display: flex; align-items: center; justify-content: center; font-size: 10px; border: 1px solid #ddd; border-radius: 2px;">QR Yok</div>'
                }
              </div>
            `).join('')}
          </div>
        `).join('')}
      </body>
      </html>
    `;
  };

  // Zimmet kaldırma fonksiyonları
  const handleUnassign = (phone: MobilePhone) => {
    if (!phone.assigned_user_id) {
      showGlobalNotification.warning(t('mobilePhones.notifWarning'), t('mobilePhones.errNotAssigned'));
      return;
    }

    setSelectedPhoneForUnassign(phone);
    setShowUnassignConfirmModal(true);
  };

  const handleUnassignConfirm = async () => {
    setShowUnassignConfirmModal(false);
    if (selectedPhoneForUnassign) {
      try {
        const resp = await fetch(`/api/assignment-forms/phone/${selectedPhoneForUnassign.id}/latest-equipment`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          setReturnEquipment(data.delivered_equipment || []);
        }
      } catch {}
    }
    setShowReturnFormModal(true);
  };

  const handleReturnFormDecision = async (createForm: boolean) => {
    setCreateReturnForm(createForm);
    setShowReturnFormModal(false);

    if (createForm) {
      // Zimmet geri teslim formu oluştur
      await createReturnAssignmentForm();
    } else {
      // Sadece atamayı kaldır
      await unassignPhone();
    }
    setReturnFormPhotos([]);
  };

  const uploadReturnPhotos = async (formId: number, photos: string[]) => {
    if (photos.length === 0) return;
    const fd = new FormData();
    photos.forEach((dataUrl, i) => {
      const arr = dataUrl.split(',');
      const mime = arr[0].match(/:(.*?);/)![1];
      const bstr = atob(arr[1]);
      const u8arr = new Uint8Array(bstr.length);
      for (let n = 0; n < bstr.length; n++) u8arr[n] = bstr.charCodeAt(n);
      fd.append('photos', new Blob([u8arr], { type: mime }), `photo-${i + 1}.jpg`);
    });
    await fetch(`/api/assignment-forms/${formId}/return-photos`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
      body: fd
    });
  };

  const addReturnFormPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const remaining = 4 - returnFormPhotos.length;
    const toProcess = Math.min(files.length, remaining);
    for (let i = 0; i < toProcess; i++) {
      const file = files[i];
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxW = 1200, maxH = 1600;
          let { width, height } = img;
          if (width > maxW || height > maxH) {
            const ratio = Math.min(maxW / width, maxH / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
          setReturnFormPhotos(prev => prev.length < 4 ? [...prev, dataUrl] : prev);
        };
        img.src = ev.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const createReturnAssignmentForm = async () => {
    if (!selectedPhoneForUnassign) return;

    try {
      // Tarih formatını MySQL DATETIME formatına dönüştür (YYYY-MM-DD HH:MM:SS)
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      const mysqlDateTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

      // Önce zimmet geri teslim formu oluştur
      const formResponse = await fetch('/api/assignment-forms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          mobile_phone_id: selectedPhoneForUnassign.id,
          assigned_user_id: selectedPhoneForUnassign.assigned_user_id,
          assigned_by_user_id: JSON.parse(localStorage.getItem('user') || '{}').id,
          assignment_date: mysqlDateTime,
          form_type: 'return',
          notes: returnFormNotes || 'Zimmet geri teslim işlemi',
          received_equipment: returnEquipment.length > 0 ? returnEquipment : undefined,
        })
      });

      if (!formResponse.ok) {
        const errorData = await formResponse.json().catch(() => ({}));
        console.error('Form creation error:', errorData);
        throw new Error(errorData.error || errorData.details || t('mobilePhones.errReturnFormFailed'));
      }

      const formData = await formResponse.json();

      // Fotoğrafları yükle
      if (returnFormPhotos.length > 0) {
        await uploadReturnPhotos(formData.id, returnFormPhotos);
      }

      // Şimdi telefonu atamadan kaldır
      await unassignPhone();

      showGlobalNotification.success(
        t('mobilePhones.notifSuccess'),
        `${t('mobilePhones.returnFormCreated')} (${t('mobilePhones.formNo')}: ${formData.form_number})`
      );

      // Zimmet formları sayfasına yönlendir
      window.location.href = '/assignment-forms';

    } catch (error: any) {
      showGlobalNotification.error(t('mobilePhones.notifError'), error.message);
    }
  };

  const unassignPhone = async () => {
    if (!selectedPhoneForUnassign) return;

    try {
      const response = await fetch(`/api/mobile-phones/${selectedPhoneForUnassign.id}/unassign`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          notes: returnFormNotes || 'Zimmet kaldırma işlemi'
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || t('mobilePhones.errUnassignFailed'));
      }

      showGlobalNotification.success(
        t('mobilePhones.notifSuccess'),
        `${selectedPhoneForUnassign.brand} ${selectedPhoneForUnassign.model} ${t('mobilePhones.unassignedSuccess')}`
      );

      // State'leri temizle
      setSelectedPhoneForUnassign(null);
      setReturnFormNotes('');
      setReturnEquipment([]);
      setCreateReturnForm(false);
      loadData();
    } catch (error: any) {
      showGlobalNotification.error(t('mobilePhones.notifError'), error.message);
    }
  };

  const handleShowHistory = async (phone: MobilePhone) => {
    setLoadingHistory(true);
    setSelectedPhoneForHistory(phone);
    setShowHistoryModal(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/mobile-phones/${phone.id}/history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        const history = await response.json();
        setPhoneHistory(history);
      } else {
        const errorData = await response.json();
        showGlobalNotification.error(t('mobilePhones.notifError'), errorData.error || t('mobilePhones.errHistoryLoad'));
        setPhoneHistory([]);
      }
    } catch (error) {
      console.error('History fetch error:', error);
      showGlobalNotification.error(t('mobilePhones.notifError'), t('mobilePhones.errHistoryLoad'));
      setPhoneHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleDelete = async (phone: MobilePhone) => {
    if (!confirm(
      language === 'en'
        ? `Are you sure you want to delete ${phone.brand} ${phone.model} (${phone.inventory_number})?`
        : `${phone.brand} ${phone.model} (${phone.inventory_number}) cep telefonunu silmek istediğinizden emin misiniz?`
    )) {
      return;
    }

    try {
      const response = await fetch(`/api/mobile-phones/${phone.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || t('mobilePhones.errDeleteFailed'));
      }

      showGlobalNotification.success(t('mobilePhones.notifSuccess'), t('mobilePhones.phoneDeleted'));
      loadData();
    } catch (error: any) {
      showGlobalNotification.error(t('mobilePhones.notifError'), error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      inventory_number: 'TR-MOB-',
      creation_date: '',
      company: '',
      exchange_rate: '',
      currency: '',
      brand: '',
      model: '',
      color: '',
      storage: '',
      imei1: '',
      imei2: '',
      serial_number: '',
      subscription_number: '',
      gsm_number: '',
      location_id: '',
      company_id: '',
      supplier_id: '',
      contract_start_date: '',
      contract_end_date: '',
      contract_price: '',
      monthly_payment: '',
      assigned_user_id: '',
      status: 'active',
      notes: '',
      ownership: ''
    });
    setEditingPhone(null);
    setInventoryAvailable(null);
    setImei1Available(null);
    setImei2Available(null);
    setImei1ConflictDevice(null);
    setImei2ConflictDevice(null);
    setGsmAvailable(null);
    setGsmConflictDevice(null);
    setUserHasPhone(null);
    setUserConflictDevice(null);
  };

  const checkGsm = async (gsm: string) => {
    if (!gsm || gsm.length < 10) {
      setGsmAvailable(null);
      setGsmConflictDevice(null);
      return;
    }

    setCheckingGsm(true);
    try {
      const response = await fetch(`/api/mobile-phones/check-gsm/${encodeURIComponent(gsm)}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await response.json();
      
      setGsmAvailable(data.available);
      setGsmConflictDevice(data.device || null);
    } catch (error) {
      console.error('GSM check error:', error);
      setGsmAvailable(null);
      setGsmConflictDevice(null);
    } finally {
      setCheckingGsm(false);
    }
  };

  const checkUserPhone = async (userId: string) => {
    if (!userId || userId === '0') {
      setUserHasPhone(null);
      setUserConflictDevice(null);
      return;
    }

    setCheckingUser(true);
    try {
      const response = await fetch(`/api/mobile-phones/check-user/${userId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await response.json();
      
      setUserHasPhone(data.hasPhone);
      setUserConflictDevice(data.device || null);
    } catch (error) {
      console.error('User phone check error:', error);
      setUserHasPhone(null);
      setUserConflictDevice(null);
    } finally {
      setCheckingUser(false);
    }
  };

  const checkImei = async (imei: string, imeiType: 'imei1' | 'imei2') => {
    if (!imei || imei.length < 15) {
      if (imeiType === 'imei1') {
        setImei1Available(null);
        setImei1ConflictDevice(null);
      } else {
        setImei2Available(null);
        setImei2ConflictDevice(null);
      }
      return;
    }

    if (imeiType === 'imei1') {
      setCheckingImei1(true);
    } else {
      setCheckingImei2(true);
    }

    try {
      // Düzenleme modunda ise, mevcut telefonun ID'sini hariç tut
      // editingPhone null ise yeni kayıt modunda, ID varsa düzenleme modunda
      const excludeId = editingPhone?.id;
      const mode = excludeId ? 'EDIT' : 'NEW';
      
      console.log('[IMEI CHECK] Starting:', {
        imei,
        imeiType,
        excludeId,
        editingPhone: editingPhone ? { id: editingPhone.id, inventory: editingPhone.inventory_number } : null,
        mode
      });
      
      // Yeni kayıt modunda exclude_id gönderme
      if (!excludeId) {
        const url = `/api/mobile-phones/check-imei/${encodeURIComponent(imei)}`;
        console.log('[IMEI CHECK] NEW mode - url:', url);
        
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        });
        
        console.log('[IMEI CHECK] Response status:', response.status, response.statusText);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('[IMEI CHECK] API Error:', {
            status: response.status,
            statusText: response.statusText,
            body: errorText
          });
          throw new Error(`API Error: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('[IMEI CHECK] Response data:', data);
        
        if (imeiType === 'imei1') {
          setImei1Available(data.available);
          setImei1ConflictDevice(data.device || null);
          if (!data.available && data.device) {
            console.warn('[IMEI CHECK] IMEI1 conflict:', data.device);
          }
        } else {
          setImei2Available(data.available);
          setImei2ConflictDevice(data.device || null);
          if (!data.available && data.device) {
            console.warn('[IMEI CHECK] IMEI2 conflict:', data.device);
          }
        }
        return;
      }
      
      // Düzenleme modunda exclude_id ile kontrol et
      const url = `/api/mobile-phones/check-imei/${encodeURIComponent(imei)}?exclude_id=${excludeId}`;
      console.log('[IMEI CHECK] EDIT mode - excludeId:', excludeId, 'url:', url);
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      console.log('[IMEI CHECK] Response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[IMEI CHECK] API Error:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('[IMEI CHECK] Response data:', data);
      
      // Response'u işle (sadece düzenleme modunda buraya gelir)
      if (imeiType === 'imei1') {
        setImei1Available(data.available);
        setImei1ConflictDevice(data.device || null);
        if (!data.available && data.device) {
          console.warn('[IMEI CHECK] IMEI1 conflict:', data.device);
        }
      } else {
        setImei2Available(data.available);
        setImei2ConflictDevice(data.device || null);
        if (!data.available && data.device) {
          console.warn('[IMEI CHECK] IMEI2 conflict:', data.device);
        }
      }
    } catch (error) {
      console.error('[IMEI CHECK] Error:', {
        message: error.message,
        stack: error.stack,
        imei,
        imeiType,
        editingPhone: editingPhone ? { id: editingPhone.id } : null
      });
      if (imeiType === 'imei1') {
        setImei1Available(null);
        setImei1ConflictDevice(null);
      } else {
        setImei2Available(null);
        setImei2ConflictDevice(null);
      }
    } finally {
      if (imeiType === 'imei1') {
        setCheckingImei1(false);
      } else {
        setCheckingImei2(false);
      }
    }
  };

  const openEditModal = (phone: MobilePhone) => {
    setFormData({
      inventory_number: phone.inventory_number,
      creation_date: phone.creation_date ? phone.creation_date.split('T')[0] : '',
      exchange_rate: phone.exchange_rate?.toString() || '',
      agreement_year: phone.agreement_year || '',
      currency: phone.currency || '',
      brand: phone.brand,
      model: phone.model,
      color: phone.color || '',
      storage: phone.storage || '',
      imei1: phone.imei1,
      imei2: phone.imei2 || '',
      serial_number: phone.serial_number || '',
      subscription_number: phone.subscription_number || '',
      gsm_number: phone.gsm_number || '',
      location_id: phone.location_id?.toString() || '',
      company_id: phone.company_id?.toString() || '',
      supplier_id: phone.supplier_id?.toString() || '',
      contract_start_date: phone.contract_start_date ? phone.contract_start_date.split('T')[0] : '',
      contract_end_date: phone.contract_end_date ? phone.contract_end_date.split('T')[0] : '',
      contract_price: formatLocalizedNumber(phone.contract_price),
      monthly_payment: formatLocalizedNumber(phone.monthly_payment),
      assigned_user_id: phone.assigned_user_id?.toString() || '',
      status: phone.status,
      notes: phone.notes || '',
      ownership: phone.ownership || 'rental'
    });
    setEditingPhone(phone);
    setShowModal(true);
  };

  const handleQuickCopy = (phone: MobilePhone) => {
    // Önce state'leri temizle - React state güncellemeleri asenkron olduğu için önce null yapıyoruz
    setEditingPhone(null);
    setImei1Available(null);
    setImei2Available(null);
    setImei1ConflictDevice(null);
    setImei2ConflictDevice(null);
    setInventoryAvailable(null);
    
    // Sonra formData'yı ayarla
    setFormData({
      inventory_number: 'TR-MOB-',
      creation_date: phone.creation_date ? phone.creation_date.split('T')[0] : '',
      exchange_rate: phone.exchange_rate?.toString() || '',
      agreement_year: phone.agreement_year || '',
      currency: phone.currency || '',
      brand: phone.brand,
      model: phone.model,
      color: phone.color || '',
      storage: phone.storage || '',
      imei1: '', // Kopyalanmayacak
      imei2: '', // Kopyalanmayacak
      serial_number: '', // Kopyalanmayacak
      subscription_number: '', // Kopyalanmayacak
      gsm_number: '', // GSM numarası da kopyalanmayacak (çünkü benzersiz olmalı)
      location_id: phone.location_id?.toString() || '',
      company_id: phone.company_id?.toString() || '',
      supplier_id: phone.supplier_id?.toString() || '',
      contract_start_date: phone.contract_start_date ? phone.contract_start_date.split('T')[0] : '',
      contract_end_date: phone.contract_end_date ? phone.contract_end_date.split('T')[0] : '',
      contract_price: formatLocalizedNumber(phone.contract_price),
      monthly_payment: formatLocalizedNumber(phone.monthly_payment),
      assigned_user_id: '', // Kopyalanmayacak
      status: 'available', // Kopyalanan telefonlar her zaman Atanmamış olarak başlamalı
      notes: phone.notes || '',
      ownership: phone.ownership || 'rental'
    });
    
    setShowModal(true);
    showGlobalNotification.info(t('mobilePhones.notifInfo'), t('mobilePhones.phoneCopied'));
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'assigned': return <CheckCircle className="w-4 h-4 text-blue-600" />;
      case 'available': return <CheckCircle className="w-4 h-4 text-gray-500 dark:text-gray-400" />;
      case 'reserved': return <Clock className="w-4 h-4 text-amber-600" />;
      case 'inactive': return <XCircle className="w-4 h-4 text-gray-600 dark:text-gray-400" />;
      case 'damaged': return <AlertCircle className="w-4 h-4 text-red-600" />;
      case 'lost': return <AlertCircle className="w-4 h-4 text-orange-600" />;
      default: return <CheckCircle className="w-4 h-4 text-green-600" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return t('mobilePhones.statusActive');
      case 'assigned': return t('mobilePhones.statusAssigned');
      case 'available': return t('mobilePhones.statusAvailable');
      case 'reserved': return t('mobilePhones.statusReserved');
      case 'inactive': return t('mobilePhones.statusInactive');
      case 'damaged': return t('mobilePhones.statusDamaged');
      case 'lost': return t('mobilePhones.statusLost');
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  const pageIds = paginatedPhones.map(p => p.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id));
  const togglePageSelection = () => setSelectedIds(prev => allPageSelected
    ? prev.filter(id => !pageIds.includes(id))
    : Array.from(new Set([...prev, ...pageIds])));
  const isArchiveView = viewMode === 'archived';

  // Satır işlem butonları: masaüstü tablo ve mobil kart görünümünde ortak
  const renderPhoneActions = (phone: MobilePhone, variant: 'table' | 'card') => {
    const btn = variant === 'card'
      ? 'min-h-[44px] min-w-[44px] rounded-lg flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700'
      : 'p-1 min-h-[32px] min-w-[32px] flex items-center justify-center';
    const icon = variant === 'card' ? 'w-5 h-5' : 'w-4 h-4';
    return (
      <>
      {isArchiveView ? (
      <>
      <button
        onClick={() => handleShowHistory(phone)}
        className={`${btn} text-primary hover:text-primary-dark transition-colors`}
        title={t('mobilePhones.titleHistory')}
      >
        <Clock className={icon} />
      </button>
      <button
        onClick={() => openArchiveDialog('unarchive', [phone.id])}
        className={`${btn} text-green-600 hover:text-green-800 transition-colors`}
        title={t('mobilePhones.archive.restore')}
      >
        <ArchiveRestore className={icon} />
      </button>
      </>
      ) : (
      <>
      {/* Kullanıcı Atama Butonu */}
      <button
        onClick={() => handleQuickAssign(phone)}
        className={`${btn} transition-colors ${
          checkContractStatus(phone).isExpired
            ? 'text-gray-400 cursor-not-allowed'
            : 'text-green-600 hover:text-green-900'
        }`}
        title={
          checkContractStatus(phone).isExpired
            ? t('mobilePhones.titleContractExpiredNoAssign')
            : t('mobilePhones.titleQuickAssign')
        }
        disabled={checkContractStatus(phone).isExpired}
      >
        <UserPlus className={icon} />
      </button>
      
      {/* Zimmet Kaldırma Butonu - Sadece atanmış telefonlarda göster */}
      {phone.assigned_user_id && (
        <button
          onClick={() => handleUnassign(phone)}
          className={`${btn} text-orange-600 hover:text-orange-900 transition-colors`}
          title={t('mobilePhones.titleUnassign')}
        >
          <UserMinus className={icon} />
        </button>
      )}
      <button
        onClick={() => {
          setSelectedPhoneForLabel(phone);
          setShowLabelModal(true);
          loadQRCodeForLabel(phone);
        }}
        className={`${btn} text-purple-600 hover:text-purple-900 transition-colors`}
        title={t('mobilePhones.titlePrintLabel')}
      >
        <FileText className={icon} />
      </button>
      <button
        onClick={() => handleQuickCopy(phone)}
        className={`${btn} text-orange-600 hover:text-orange-900 transition-colors`}
        title={t('mobilePhones.titleQuickCopy')}
      >
        <Copy className={icon} />
      </button>
      <button
        onClick={() => openEditModal(phone)}
        className={`${btn} text-indigo-600 hover:text-indigo-900 transition-colors`}
        title={t('mobilePhones.titleEdit')}
      >
        <Edit className={icon} />
      </button>
      <button
        onClick={() => handleShowHistory(phone)}
        className={`${btn} text-primary hover:text-primary-dark transition-colors`}
        title={t('mobilePhones.titleHistory')}
      >
        <Clock className={icon} />
      </button>
      <button
        onClick={() => openArchiveDialog('archive', [phone.id])}
        className={`${btn} text-amber-600 hover:text-amber-800 transition-colors`}
        title={t('mobilePhones.archive.archive')}
      >
        <Archive className={icon} />
      </button>
      <button
        onClick={() => handleDelete(phone)}
        className={`${btn} ${variant === 'card' ? 'ml-auto' : ''} text-red-600 hover:text-red-900 transition-colors`}
        title={t('mobilePhones.titleDelete')}
      >
        <Trash2 className={icon} />
      </button>
      </>
      )}
      </>
    );
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('mobilePhones.title')}</h1>
            <p className="text-gray-600 dark:text-gray-400">{t('mobilePhones.subtitle')}</p>
          </div>
          <div className="grid grid-cols-3 sm:flex gap-2 w-full sm:w-auto">
            <button
              onClick={() => setViewMode(isArchiveView ? 'active' : 'archived')}
              aria-pressed={isArchiveView}
              className={`col-span-3 sm:col-span-1 flex items-center justify-center whitespace-nowrap px-2 sm:px-4 py-3 border rounded-lg transition-colors min-h-[44px] text-sm font-medium ${
                isArchiveView
                  ? 'border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-700'
                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {isArchiveView ? <Smartphone className="w-4 h-4 mr-2" /> : <Archive className="w-4 h-4 mr-2" />}
              {isArchiveView
                ? t('mobilePhones.archive.backToActive')
                : t('mobilePhones.archive.viewArchive', { count: archivedPhones.length })}
            </button>
            <button
              onClick={() => setShowBulkImportModal(true)}
              className="flex items-center justify-center whitespace-nowrap px-2 sm:px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors min-h-[44px] text-sm font-medium"
            >
              <Upload className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">{t('mobilePhones.btnBulkImport')}</span>
              <span className="sm:hidden">{t('mobilePhones.btnImport')}</span>
            </button>
            <button
              onClick={() => setShowBulkLabelModal(true)}
              className="flex items-center justify-center whitespace-nowrap px-2 sm:px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors min-h-[44px] text-sm font-medium"
            >
              <QrCode className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">{t('mobilePhones.btnBulkLabel')}</span>
              <span className="sm:hidden">{t('mobilePhones.btnLabel')}</span>
            </button>
            <button
              onClick={exportToExcel}
              className="flex items-center justify-center whitespace-nowrap px-2 sm:px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors min-h-[44px] text-sm font-medium"
            >
              <Download className="w-4 h-4 mr-2" />
              Excel
            </button>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="col-span-3 order-first sm:order-none flex items-center justify-center px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors min-h-[44px] text-sm font-medium"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('mobilePhones.btnNewPhone')}
            </button>
          </div>
        </div>
      </div>

      {/* Arşiv görünümü bilgisi */}
      {isArchiveView && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-100">
          <Archive className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <div>
            <p className="font-medium">{t('mobilePhones.archive.bannerTitle', { count: archivedPhones.length })}</p>
            <p className="mt-1 text-amber-800/80 dark:text-amber-200/80">{t('mobilePhones.archive.bannerText')}</p>
          </div>
        </div>
      )}

      {/* Stats - kartlar hızlı filtre olarak çalışır; aynı anda tek kart filtresi uygulanır */}
      {!isArchiveView && (() => {
        type StatCard = 'total' | 'available' | 'assigned' | 'unassigned' | 'expiring';
        const onlyOne = (s: string, a: string, c: string) =>
          statusFilter === s && assignmentFilter === a && contractExpirationFilter === c;
        const activeCard: StatCard | null =
          onlyOne('all', 'all', 'all') ? 'total'
          : onlyOne('available', 'all', 'all') ? 'available'
          : onlyOne('all', 'assigned', 'all') ? 'assigned'
          : onlyOne('all', 'unassigned', 'all') ? 'unassigned'
          : onlyOne('all', 'all', 'expires_month') ? 'expiring'
          : null;

        const applyCard = (card: StatCard) => {
          const reset = card === 'total' || card === activeCard;
          setStatusFilter(!reset && card === 'available' ? 'available' : 'all');
          setAssignmentFilter(!reset && (card === 'assigned' || card === 'unassigned') ? card : 'all');
          setContractExpirationFilter(!reset && card === 'expiring' ? 'expires_month' : 'all');
          if (card === 'total') {
            setSearchTerm('');
            setLocationFilter('all');
            setModelFilter('all');
          }
        };

        const cards: { key: StatCard; label: string; value: number; valueClass: string; icon: React.ReactNode }[] = [
          { key: 'total', label: t('mobilePhones.statTotal'), value: phones.length, valueClass: 'text-gray-900 dark:text-gray-100', icon: <Smartphone className="w-4 h-4 sm:w-8 sm:h-8 text-indigo-600" /> },
          { key: 'available', label: t('mobilePhones.statusAvailable'), value: phones.filter(p => p.status === 'available').length, valueClass: 'text-green-600', icon: <CheckCircle className="w-4 h-4 sm:w-8 sm:h-8 text-green-600" /> },
          { key: 'assigned', label: t('mobilePhones.statAssigned'), value: phones.filter(p => p.assigned_user_id).length, valueClass: 'text-primary', icon: <User className="w-4 h-4 sm:w-8 sm:h-8 text-primary" /> },
          { key: 'unassigned', label: t('mobilePhones.statUnassigned'), value: phones.filter(p => !p.assigned_user_id).length, valueClass: 'text-gray-600 dark:text-gray-400', icon: <User className="w-4 h-4 sm:w-8 sm:h-8 text-gray-600 dark:text-gray-400" /> },
          { key: 'expiring', label: t('mobilePhones.statContractExpiring'), value: phones.filter(p => matchesContractFilter(p, 'expires_month')).length, valueClass: 'text-orange-600', icon: <Calendar className="w-4 h-4 sm:w-8 sm:h-8 text-orange-600" /> }
        ];

        return (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6 mb-6">
            {cards.map((card, idx) => (
              <button
                key={card.key}
                type="button"
                onClick={() => applyCard(card.key)}
                aria-pressed={activeCard === card.key}
                className={`text-left bg-white dark:bg-gray-800 rounded-lg shadow-sm border p-3 sm:p-6 hover:shadow-md transition-all min-h-[80px] sm:min-h-[100px] ${
                  activeCard === card.key && card.key !== 'total'
                    ? 'border-primary ring-2 ring-primary/30'
                    : 'border-gray-200 dark:border-gray-700'
                } ${idx === cards.length - 1 ? 'col-span-2 sm:col-span-1' : ''}`}
              >
                <div className="flex flex-col sm:flex-row items-center justify-between h-full">
                  <div className="text-center sm:text-left">
                    <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{card.label}</p>
                    <p className={`text-lg sm:text-2xl font-bold ${card.valueClass}`}>{card.value}</p>
                  </div>
                  {card.icon}
                </div>
              </button>
            ))}
          </div>
        );
      })()}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="grid grid-cols-1 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={t('mobilePhones.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent w-full min-h-[44px]"
            />
          </div>

          <div>
          <CollapsibleFilters
            activeCount={[statusFilter, assignmentFilter, locationFilter, modelFilter, contractExpirationFilter].filter(f => f !== 'all').length}
            onClear={() => {
              setStatusFilter('all');
              setAssignmentFilter('all');
              setLocationFilter('all');
              setModelFilter('all');
              setContractExpirationFilter('all');
            }}
            desktopClassName="md:grid md:grid-cols-3 lg:grid-cols-5 md:gap-4"
          >
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent min-h-[44px]"
          >
            <option value="all">{t('mobilePhones.filterAllStatuses')}</option>
            <option value="assigned">{t('mobilePhones.statusAssigned')}</option>
            <option value="available">{t('mobilePhones.statusAvailable')}</option>
            <option value="reserved">{t('mobilePhones.statusReserved')}</option>
            <option value="active">{t('mobilePhones.statusActive')}</option>
            <option value="inactive">{t('mobilePhones.statusInactive')}</option>
            <option value="damaged">{t('mobilePhones.statusDamaged')}</option>
            <option value="lost">{t('mobilePhones.statusLost')}</option>
          </select>

          <select
            value={assignmentFilter}
            onChange={(e) => setAssignmentFilter(e.target.value)}
            className="px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent min-h-[44px]"
          >
            <option value="all">{t('mobilePhones.filterAllAssignments')}</option>
            <option value="assigned">{t('mobilePhones.statAssigned')}</option>
            <option value="unassigned">{t('mobilePhones.statUnassigned')}</option>
          </select>

          <select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent min-h-[44px]"
          >
            <option value="all">{t('mobilePhones.filterAllLocations')}</option>
            {locations.map(location => (
              <option key={location.id} value={location.id}>{location.name}</option>
            ))}
          </select>

          <select
            value={modelFilter}
            onChange={(e) => setModelFilter(e.target.value)}
            className="px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent min-h-[44px]"
          >
            <option value="all">{t('mobilePhones.filterAllModels')}</option>
            {uniqueModels.map(model => (
              <option key={model} value={model}>{model}</option>
            ))}
          </select>

          <select
            value={contractExpirationFilter}
            onChange={(e) => setContractExpirationFilter(e.target.value)}
            className="px-3 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent min-h-[44px]"
          >
            <option value="all">{t('mobilePhones.filterContractStatus')}</option>
            <option value="expired">{t('mobilePhones.filterExpired')}</option>
            <option value="expires_today">{t('mobilePhones.filterExpiresToday')}</option>
            <option value="expires_week">{t('mobilePhones.filterExpiresWeek')}</option>
            <option value="expires_month">{t('mobilePhones.filterExpiresMonth')}</option>
            <option value="expires_3months">{t('mobilePhones.filterExpires3Months')}</option>
            <option value="no_contract">{t('mobilePhones.filterNoContract')}</option>
          </select>
          </CollapsibleFilters>
          </div>
        </div>
      </div>

      {/* Toplu işlem çubuğu */}
      {selectedIds.length > 0 && (
        <div className="sticky top-2 z-10 mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-primary/30 bg-white px-4 py-3 text-sm shadow-sm dark:bg-gray-800">
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {t('mobilePhones.archive.selected', { count: selectedIds.length })}
          </span>
          {selectedIds.length < filteredPhones.length && (
            <button
              type="button"
              onClick={() => setSelectedIds(filteredPhones.map(p => p.id))}
              className="text-primary hover:underline"
            >
              {t('mobilePhones.archive.selectAllFiltered', { count: filteredPhones.length })}
            </button>
          )}
          <button
            type="button"
            onClick={() => setSelectedIds([])}
            className="text-gray-500 hover:underline dark:text-gray-400"
          >
            {t('mobilePhones.archive.clearSelection')}
          </button>
          <div className="w-full sm:ml-auto sm:w-auto">
            {isArchiveView ? (
              <button
                type="button"
                onClick={() => openArchiveDialog('unarchive', selectedIds)}
                className="flex w-full items-center justify-center rounded-lg bg-primary px-4 py-2 font-medium text-white hover:bg-primary-dark min-h-[40px]"
              >
                <ArchiveRestore className="mr-2 h-4 w-4" />
                {t('mobilePhones.archive.restoreSelected', { count: selectedIds.length })}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => openArchiveDialog('archive', selectedIds)}
                className="flex w-full items-center justify-center rounded-lg bg-amber-600 px-4 py-2 font-medium text-white hover:bg-amber-700 min-h-[40px]"
              >
                <Archive className="mr-2 h-4 w-4" />
                {t('mobilePhones.archive.archiveSelected', { count: selectedIds.length })}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Mobil kart görünümü */}
        <div className="md:hidden">
          {paginatedPhones.length > 0 && (
            <label className="flex items-center gap-3 px-4 py-3 min-h-[44px] border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={allPageSelected}
                onChange={togglePageSelection}
                className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary"
              />
              {t('mobilePhones.archive.selectPage')}
            </label>
          )}
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {paginatedPhones.map((phone) => {
              const phoneModel = models.find((m: any) => m.name === phone.model && (m.brand_name === phone.brand || m.brand?.name === phone.brand));
              return (
                <div key={phone.id} className={`p-4 ${selectedIds.includes(phone.id) ? 'bg-primary/5' : ''}`}>
                  <div className="flex items-start gap-3">
                    <label className="-m-2 p-2 cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(phone.id)}
                        onChange={() => toggleSelected(phone.id)}
                        aria-label={`${phone.brand} ${phone.model} ${phone.inventory_number}`}
                        className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                    </label>
                    {phoneModel?.image_url ? (
                      <img src={phoneModel.image_url} alt={phone.model} className="w-10 h-10 flex-shrink-0 object-contain rounded-lg bg-white border border-gray-200" />
                    ) : (
                      <div className="w-10 h-10 flex-shrink-0 bg-green-100 rounded-lg flex items-center justify-center">
                        <Smartphone className="w-5 h-5 text-green-600" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">
                        {phone.brand} {phone.model}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 break-words">
                        {phone.inventory_number}
                        {phone.color && ` • ${phone.color}`}
                        {phone.storage && ` • ${phone.storage}`}
                      </div>
                    </div>
                    {!isArchiveView && (
                      <div className="flex items-center flex-shrink-0 text-xs text-gray-900 dark:text-gray-100">
                        {getStatusIcon(phone.status)}
                        <span className="ml-1">{getStatusText(phone.status)}</span>
                      </div>
                    )}
                  </div>

                  <dl className="mt-3 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-sm">
                    <dt className="text-gray-500 dark:text-gray-400">IMEI</dt>
                    <dd className="text-gray-900 dark:text-gray-100 break-all">
                      {phone.imei1}{phone.imei2 && <span className="text-gray-500 dark:text-gray-400"> / {phone.imei2}</span>}
                    </dd>
                    {phone.serial_number && (
                      <>
                        <dt className="text-gray-500 dark:text-gray-400">{t('mobilePhones.colSerialNo')}</dt>
                        <dd className="text-gray-900 dark:text-gray-100 break-all">{phone.serial_number}</dd>
                      </>
                    )}
                    {phone.subscription_number && (
                      <>
                        <dt className="text-gray-500 dark:text-gray-400">{t('mobilePhones.colSubscriptionNo')}</dt>
                        <dd className="text-gray-900 dark:text-gray-100 break-all">{phone.subscription_number}</dd>
                      </>
                    )}
                    {(phone.gsm_number || phone.archived_gsm_number) && (
                      <>
                        <dt className="text-gray-500 dark:text-gray-400">{t('mobilePhones.colGsmNo')}</dt>
                        <dd className="text-gray-900 dark:text-gray-100">
                          {phone.gsm_number || (
                            <><span className="line-through">{phone.archived_gsm_number}</span> ({t('mobilePhones.archive.gsmRemovedShort')})</>
                          )}
                        </dd>
                      </>
                    )}
                    {(phone.location_name || phone.company_name) && (
                      <>
                        <dt className="text-gray-500 dark:text-gray-400">{t('mobilePhones.colLocation')}</dt>
                        <dd className="text-gray-900 dark:text-gray-100 break-words">
                          {[phone.location_name, phone.company_name].filter(Boolean).join(' • ')}
                        </dd>
                      </>
                    )}
                    {isArchiveView ? (
                      <>
                        <dt className="text-gray-500 dark:text-gray-400">{t('mobilePhones.archive.colArchiveInfo')}</dt>
                        <dd className="text-gray-900 dark:text-gray-100">
                          {phone.archived_at ? new Date(phone.archived_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR') : '-'}
                          {phone.archived_by_name && <span className="text-gray-500 dark:text-gray-400"> • {phone.archived_by_name}</span>}
                        </dd>
                        {phone.archive_reason && (
                          <>
                            <dt className="text-gray-500 dark:text-gray-400">{t('mobilePhones.archive.colReason')}</dt>
                            <dd className="text-gray-700 dark:text-gray-300 break-words">{phone.archive_reason}</dd>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <dt className="text-gray-500 dark:text-gray-400">{t('mobilePhones.colAssignedUser')}</dt>
                        <dd className="text-gray-900 dark:text-gray-100 break-words">
                          {phone.assigned_user_name || <span className="text-gray-500 dark:text-gray-400">{t('mobilePhones.statusInactive')}</span>}
                        </dd>
                      </>
                    )}
                  </dl>

                  <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700 flex flex-wrap items-center gap-1">
                    {renderPhoneActions(phone, 'card')}
                  </div>
                </div>
              );
            })}
            {paginatedPhones.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                {isArchiveView ? t('mobilePhones.archive.empty') : t('mobilePhones.archive.noResults')}
              </div>
            )}
          </div>
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full table-auto divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="pl-4 pr-2 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    onChange={togglePageSelection}
                    aria-label={t('mobilePhones.archive.selectPage')}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('mobilePhones.colDeviceInfo')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  IMEI
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('mobilePhones.colSerialNo')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('mobilePhones.colSubscriptionNo')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('mobilePhones.colLocationCompany')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {isArchiveView ? t('mobilePhones.archive.colArchiveInfo') : t('mobilePhones.colAssignedUser')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {isArchiveView ? t('mobilePhones.archive.colReason') : t('mobilePhones.colStatus')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('mobilePhones.colActions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedPhones.map((phone) => {
                const phoneModel = models.find((m: any) => m.name === phone.model && (m.brand_name === phone.brand || m.brand?.name === phone.brand));
                return (
                <tr key={phone.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 ${selectedIds.includes(phone.id) ? 'bg-primary/5' : ''}`}>
                  <td className="pl-4 pr-2 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(phone.id)}
                      onChange={() => toggleSelected(phone.id)}
                      aria-label={`${phone.brand} ${phone.model} ${phone.inventory_number}`}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  </td>
                  <td className="px-6 py-3">
                    <div>
                      <div className="flex items-center">
                        {phoneModel?.image_url ? (
                          <img src={phoneModel.image_url} alt={phone.model} className="w-10 h-10 object-contain rounded-lg bg-white mr-3 border border-gray-200" />
                        ) : (
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                            <Smartphone className="w-5 h-5 text-green-600" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {phone.brand} {phone.model}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                            {phone.inventory_number}
                            {phone.color && ` • ${phone.color}`}
                            {phone.storage && ` • ${phone.storage}`}
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">{phone.imei1}</div>
                    {phone.imei2 && (
                      <div className="text-sm text-gray-500 dark:text-gray-400">{phone.imei2}</div>
                    )}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">{phone.serial_number || '-'}</div>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">{phone.subscription_number || '-'}</div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {phone.location_name && (
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 text-gray-400 mr-1" />
                          <span className="truncate">{phone.location_name}</span>
                        </div>
                      )}
                      {phone.company_name && (
                        <div className="flex items-center text-gray-500 dark:text-gray-400">
                          <Building className="w-4 h-4 text-gray-400 mr-1" />
                          <span className="truncate">{phone.company_name}</span>
                        </div>
                      )}
                      {phone.supplier_name && (
                        <div className="flex items-center text-gray-500 dark:text-gray-400">
                          <Truck className="w-4 h-4 text-gray-400 mr-1" />
                          <span className="truncate">{phone.supplier_name}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  {isArchiveView ? (
                  <>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {phone.archived_at ? new Date(phone.archived_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR') : '-'}
                    </div>
                    {phone.archived_by_name && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">{phone.archived_by_name}</div>
                    )}
                    {phone.gsm_number ? (
                      <div className="text-xs text-gray-500 dark:text-gray-400">GSM: {phone.gsm_number}</div>
                    ) : phone.archived_gsm_number ? (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        GSM: <span className="line-through">{phone.archived_gsm_number}</span> ({t('mobilePhones.archive.gsmRemovedShort')})
                      </div>
                    ) : null}
                  </td>
                  <td className="px-6 py-3">
                    <div className="max-w-xs text-sm text-gray-700 dark:text-gray-300 break-words">
                      {phone.archive_reason || <span className="text-gray-400">—</span>}
                    </div>
                  </td>
                  </>
                  ) : (
                  <>
                  <td className="px-6 py-3 whitespace-nowrap">
                    {phone.assigned_user_name ? (
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-gray-400 mr-1" />
                        <span className="text-sm text-gray-900 dark:text-gray-100 truncate">{phone.assigned_user_name}</span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-gray-400">{t('mobilePhones.statusInactive')}</span>
                    )}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      {getStatusIcon(phone.status)}
                      <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">
                        {getStatusText(phone.status)}
                      </span>
                    </div>
                  </td>
                  </>
                  )}
                  <td className="px-6 py-3 text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-1 sm:space-x-2">
                      {renderPhoneActions(phone, 'table')}
                    </div>
                  </td>
                </tr>
              );
              })}
              {paginatedPhones.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                    {isArchiveView ? t('mobilePhones.archive.empty') : t('mobilePhones.archive.noResults')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>


        {/* Pagination */}
        {filteredPhones.length > itemsPerPage && (
          <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 min-h-[44px] border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('mobilePhones.paginationPrev')}
              </button>
              <span className="self-center text-sm text-gray-700 dark:text-gray-300">
                {t('mobilePhones.paginationPage', { current: currentPage, total: getTotalPages() })}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === getTotalPages()}
                className="relative inline-flex items-center px-4 py-2 min-h-[44px] border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('mobilePhones.paginationNext')}
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span>
                  {' - '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, filteredPhones.length)}
                  </span>
                  {' / '}
                  <span className="font-medium">{filteredPhones.length}</span>
                  {' '}{t('mobilePhones.paginationOf')}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">{t('mobilePhones.paginationPrev')}</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: getTotalPages() }, (_, i) => i + 1).map((page) => {
                    // Show first page, last page, current page, and pages around current page
                    const shouldShow = 
                      page === 1 || 
                      page === getTotalPages() || 
                      Math.abs(page - currentPage) <= 2;
                    
                    if (!shouldShow) {
                      // Show ellipsis for gaps
                      if (page === 2 && currentPage > 4) {
                        return (
                          <span key={`ellipsis-${page}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300">
                            ...
                          </span>
                        );
                      }
                      if (page === getTotalPages() - 1 && currentPage < getTotalPages() - 3) {
                        return (
                          <span key={`ellipsis-${page}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300">
                            ...
                          </span>
                        );
                      }
                      return null;
                    }
                    
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === currentPage
                            ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === getTotalPages()}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">{t('mobilePhones.paginationNext')}</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}

        {filteredPhones.length === 0 && (
          <div className="text-center py-12">
            <Smartphone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">{t('mobilePhones.emptyTitle')}</h3>
            <p className="text-gray-600 dark:text-gray-400">{t('mobilePhones.emptyDesc')}</p>
          </div>
        )}
      </div>

      {/* Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-4 sm:px-6">
              <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-4 mb-4 flex items-center justify-between bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {editingPhone ? t('mobilePhones.modalEditTitle') : t('mobilePhones.modalNewTitle')}
                </h2>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="min-h-[44px] min-w-[44px] -mr-2 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Cihaz Bilgileri */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 border-b pb-2">{t('mobilePhones.sectionDeviceInfo')}</h3>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('mobilePhones.labelInventoryNo')}
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          required
                          value={formData.inventory_number}
                          onChange={async (e) => {
                            let value = e.target.value;
                            if (!value.startsWith('TR-MOB-')) {
                              value = 'TR-MOB-' + value.replace('TR-MOB-', '');
                            }
                            setFormData({...formData, inventory_number: value});
                            
                            if (value.length > 7) {
                              setCheckingInventory(true);
                              try {
                                // Düzenleme modunda ise, mevcut telefonun ID'sini hariç tut
                                // editingPhone null ise yeni kayıt modunda, ID varsa düzenleme modunda
                                const excludeId = editingPhone?.id;
                                
                                // Yeni kayıt modunda exclude_id gönderme
                                if (!excludeId) {
                                  const url = `/api/mobile-phones/check-inventory/${encodeURIComponent(value)}`;
                                  console.log('[INVENTORY CHECK] NEW mode:', {
                                    inventoryNumber: value,
                                    url,
                                    editingPhone: editingPhone ? { id: editingPhone.id } : null
                                  });
                                  
                                  const response = await fetch(url, {
                                    headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
                                  });
                                  
                                  console.log('[INVENTORY CHECK] Response status:', response.status, response.statusText);
                                  
                                  if (!response.ok) {
                                    const errorText = await response.text();
                                    console.error('[INVENTORY CHECK] API Error:', {
                                      status: response.status,
                                      statusText: response.statusText,
                                      body: errorText
                                    });
                                    throw new Error(`API Error: ${response.status} ${response.statusText}`);
                                  }
                                  
                                  const data = await response.json();
                                  console.log('[INVENTORY CHECK] Response data:', data);
                                  
                                  setInventoryAvailable(data.available);
                                  
                                  if (!data.available) {
                                    console.warn('[INVENTORY CHECK] Inventory number conflict:', value);
                                  }
                                  
                                  setCheckingInventory(false);
                                  return;
                                }
                                
                                // Düzenleme modunda exclude_id ile kontrol et
                                const url = `/api/mobile-phones/check-inventory/${encodeURIComponent(value)}?exclude_id=${excludeId}`;
                                console.log('[INVENTORY CHECK] EDIT mode:', {
                                  inventoryNumber: value,
                                  excludeId,
                                  url,
                                  editingPhone: { id: editingPhone.id, inventory: editingPhone.inventory_number }
                                });
                                
                                const response = await fetch(url, {
                                  headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
                                });
                                
                                console.log('[INVENTORY CHECK] Response status:', response.status, response.statusText);
                                
                                if (!response.ok) {
                                  const errorText = await response.text();
                                  console.error('[INVENTORY CHECK] API Error:', {
                                    status: response.status,
                                    statusText: response.statusText,
                                    body: errorText
                                  });
                                  throw new Error(`API Error: ${response.status} ${response.statusText}`);
                                }
                                
                                const data = await response.json();
                                console.log('[INVENTORY CHECK] Response data:', data);
                                
                                setInventoryAvailable(data.available);
                                
                                if (!data.available) {
                                  console.warn('[INVENTORY CHECK] Inventory number conflict:', value);
                                }
                              } catch (error) {
                                console.error('[INVENTORY CHECK] Error:', {
                                  message: error.message,
                                  stack: error.stack,
                                  inventoryNumber: value,
                                  editingPhone: editingPhone ? { id: editingPhone.id } : null
                                });
                                setInventoryAvailable(null);
                              }
                              setCheckingInventory(false);
                            } else {
                              setInventoryAvailable(null);
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                        {checkingInventory && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                          </div>
                        )}
                      </div>
                      {inventoryAvailable === true && (
                        <p className="text-xs text-green-600 mt-1">✓ {t('mobilePhones.available')}</p>
                      )}
                      {inventoryAvailable === false && (
                        <p className="text-xs text-red-600 mt-1">✗ {t('mobilePhones.inventoryInUse')}</p>
                      )}
                    </div>

                    <div className={`grid ${formData.ownership === 'rental' ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'} gap-4`}>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('mobilePhones.labelCreationDate')}
                        </label>
                        <input
                          type="date"
                          value={formData.creation_date}
                          onChange={(e) => {
                            const creationDate = e.target.value;
                            setFormData({...formData, creation_date: creationDate});

                            // Eğer anlaşma yılı seçilmişse, sözleşme tarihlerini yeniden hesapla
                            if (creationDate && formData.agreement_year) {
                              const date = new Date(creationDate);
                              const yearCount = parseInt(formData.agreement_year);
                              if (!isNaN(yearCount)) {
                                // Sözleşme başlangıç tarihi = Oluşturma tarihi
                                const startDateStr = creationDate;

                                // Sözleşme bitiş tarihi = Oluşturma tarihi + yıl sayısı
                                const endDate = new Date(date);
                                endDate.setFullYear(date.getFullYear() + yearCount);
                                const endDateStr = endDate.toISOString().split('T')[0];

                                setFormData(prev => ({
                                  ...prev,
                                  creation_date: creationDate,
                                  contract_start_date: startDateStr,
                                  contract_end_date: endDateStr
                                }));
                              }
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                      </div>
                      {formData.ownership === 'rental' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('mobilePhones.labelAgreementYear')}
                        </label>
                        <select
                          value={formData.agreement_year}
                          onChange={(e) => {
                            const years = e.target.value;
                            setFormData({...formData, agreement_year: years});

                            // Oluşturma tarihinden sonra yıl eklenerek sözleşme tarihleri otomatik hesapla
                            if (formData.creation_date && years) {
                              const creationDate = new Date(formData.creation_date);
                              const yearCount = parseInt(years);
                              if (!isNaN(yearCount)) {
                                // Sözleşme başlangıç tarihi = Oluşturma tarihi
                                const startDateStr = formData.creation_date;

                                // Sözleşme bitiş tarihi = Oluşturma tarihi + yıl sayısı
                                const endDate = new Date(creationDate);
                                endDate.setFullYear(creationDate.getFullYear() + yearCount);
                                const endDateStr = endDate.toISOString().split('T')[0];

                                setFormData(prev => ({
                                  ...prev,
                                  agreement_year: years,
                                  contract_start_date: startDateStr,
                                  contract_end_date: endDateStr
                                }));
                              }
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        >
                          <option value="">{t('mobilePhones.optSelect')}</option>
                          <option value="1">1 {t('mobilePhones.year')}</option>
                          <option value="2">2 {t('mobilePhones.year')}</option>
                          <option value="3">3 {t('mobilePhones.year')}</option>
                          <option value="4">4 {t('mobilePhones.year')}</option>
                          <option value="5">5 {t('mobilePhones.year')}</option>
                        </select>
                      </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('mobilePhones.labelCurrency')}
                        </label>
                        <select
                          value={formData.currency}
                          onChange={(e) => setFormData({...formData, currency: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        >
                          <option value="">{t('mobilePhones.optSelect')}</option>
                          <option value="TRY">₺ {t('mobilePhones.currencyTRY')}</option>
                          <option value="USD">$ {t('mobilePhones.currencyUSD')}</option>
                          <option value="EUR">€ Euro</option>
                          <option value="GBP">£ {t('mobilePhones.currencyGBP')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('mobilePhones.labelExchangeRate')}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.exchange_rate}
                          onChange={(e) => setFormData({...formData, exchange_rate: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="34.50"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('mobilePhones.labelBrand')}
                        </label>
                        <select
                          required
                          value={formData.brand}
                          onChange={(e) => {
                            setFormData({...formData, brand: e.target.value, model: ''});
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        >
                          <option value="">{t('mobilePhones.optSelect')}</option>
                          {brands.map(brand => (
                            <option key={brand.id} value={brand.name}>{brand.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Model *
                        </label>
                        <select
                          required
                          value={formData.model}
                          onChange={(e) => setFormData({...formData, model: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          disabled={!formData.brand}
                        >
                          <option value="">Seçiniz</option>
                          {formData.brand && getModelsForBrand(formData.brand).map(model => (
                            <option key={model.id} value={model.name}>{model.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Renk
                        </label>
                        <select
                          value={formData.color}
                          onChange={(e) => setFormData({...formData, color: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        >
                          <option value="">Seçiniz</option>
                          {colorOptions.map(color => (
                            <option key={color} value={color}>{color}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Hafıza
                        </label>
                        <select
                          value={formData.storage}
                          onChange={(e) => setFormData({...formData, storage: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        >
                          <option value="">Seçiniz</option>
                          {storageOptions.map(storage => (
                            <option key={storage} value={storage}>{storage}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          IMEI 1 *
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            required
                            value={formData.imei1}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFormData({...formData, imei1: value});
                              checkImei(value, 'imei1');
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            maxLength={15}
                          />
                          {checkingImei1 && (
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                            </div>
                          )}
                        </div>
                        {imei1Available === true && (
                          <p className="text-xs text-green-600 mt-1">✓ Müsait</p>
                        )}
                        {imei1Available === false && imei1ConflictDevice && (
                          <p className="text-xs text-red-600 mt-1">
                            ✗ Bu IMEI başka bir cihazda kullanılıyor: {imei1ConflictDevice.inventory_number} ({imei1ConflictDevice.brand} {imei1ConflictDevice.model})
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          IMEI 2
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={formData.imei2}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFormData({...formData, imei2: value});
                              // IMEI2 boş bırakılabilir, sadece değer varsa kontrol et
                              if (value && value.trim()) {
                                checkImei(value.trim(), 'imei2');
                              } else {
                                setImei2Available(null);
                                setImei2ConflictDevice(null);
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            maxLength={15}
                          />
                          {checkingImei2 && (
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                            </div>
                          )}
                        </div>
                        {imei2Available === true && (
                          <p className="text-xs text-green-600 mt-1">✓ Müsait</p>
                        )}
                        {imei2Available === false && imei2ConflictDevice && (
                          <p className="text-xs text-red-600 mt-1">
                            ✗ Bu IMEI başka bir cihazda kullanılıyor: {imei2ConflictDevice.inventory_number} ({imei2ConflictDevice.brand} {imei2ConflictDevice.model})
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Seri No
                      </label>
                      <input
                        type="text"
                        value={formData.serial_number}
                        onChange={(e) => setFormData({...formData, serial_number: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        maxLength={100}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('mobilePhones.subscriptionNumber')}
                      </label>
                      <input
                        type="text"
                        value={formData.subscription_number}
                        onChange={(e) => setFormData({...formData, subscription_number: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        maxLength={100}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        GSM No
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={formData.gsm_number}
                          onChange={(e) => {
                            let value = e.target.value.replace(/\D/g, '');
                            if (value.length > 0) {
                              if (value.startsWith('90')) {
                                value = value.substring(2);
                              }
                              if (value.length <= 10) {
                                if (value.length >= 7) {
                                  value = value.replace(/(\d{3})(\d{3})(\d{2})(\d{2})/, '$1 $2 $3 $4');
                                } else if (value.length >= 4) {
                                  value = value.replace(/(\d{3})(\d{3})/, '$1 $2');
                                }
                                value = '+90 ' + value;
                              }
                            }
                            setFormData({...formData, gsm_number: value});
                            if (value) checkGsm(value);
                            else {
                              setGsmAvailable(null);
                              setGsmConflictDevice(null);
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="+90 555 123 45 67"
                          maxLength={17}
                        />
                        {checkingGsm && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Sözleşme yapılan GSM numarası</p>
                      {gsmAvailable === true && (
                        <p className="text-xs text-green-600 mt-1">✓ Müsait</p>
                      )}
                      {gsmAvailable === false && gsmConflictDevice && (gsmConflictDevice.is_archived ? (
                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                          {t('mobilePhones.archive.gsmOnArchivedHint', {
                            device: `${gsmConflictDevice.inventory_number} (${gsmConflictDevice.brand} ${gsmConflictDevice.model})`
                          })}
                        </p>
                      ) : (
                        <p className="text-xs text-red-600 mt-1">
                          ✗ Bu numara başka bir cep telefonunda kullanılıyor: {gsmConflictDevice.inventory_number} ({gsmConflictDevice.brand} {gsmConflictDevice.model})
                        </p>
                      ))}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Lokasyon
                        </label>
                        <select
                          value={formData.location_id}
                          onChange={(e) => setFormData({...formData, location_id: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        >
                          <option value="">Seçiniz</option>
                          {locations.map(location => (
                            <option key={location.id} value={location.id}>
                              {location.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Şirket
                        </label>
                        <select
                          value={formData.company_id}
                          onChange={(e) => setFormData({...formData, company_id: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        >
                          <option value="">Seçiniz</option>
                          {companies.map(company => (
                            <option key={company.id} value={company.id}>
                              {company.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Tedarikçi {suppliers.length > 0 && `(${suppliers.length})`}
                      </label>
                      <select
                        value={formData.supplier_id}
                        onChange={(e) => {
                          setFormData({...formData, supplier_id: e.target.value});
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value="">Seçiniz</option>
                        {suppliers.map(supplier => (
                          <option key={supplier.id} value={supplier.id}>
                            {supplier.name}
                          </option>
                        ))}
                      </select>
                      {suppliers.length === 0 && (
                        <p className="text-xs text-amber-600 mt-1">
                          ⚠️ Cep telefonu hizmeti veren tedarikçi bulunamadı
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Sözleşme ve Atama */}
                  <div className="space-y-4">
                    {formData.ownership === 'rental' && (<>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 border-b pb-2">Sözleşme Bilgileri</h3>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Sözleşme Başlangıç
                        </label>
                        <input
                          type="date"
                          value={formData.contract_start_date}
                          onChange={(e) => {
                            const startDate = e.target.value;
                            setFormData({...formData, contract_start_date: startDate});

                            // Eğer başlangıç tarihi ve agreement_year varsa bitiş tarihini otomatik hesapla
                            if (startDate && formData.agreement_year) {
                              const start = new Date(startDate);
                              const years = parseInt(formData.agreement_year);
                              if (!isNaN(years)) {
                                const end = new Date(start);
                                end.setFullYear(start.getFullYear() + years);
                                const endDateStr = end.toISOString().split('T')[0];
                                setFormData(prev => ({...prev, contract_start_date: startDate, contract_end_date: endDateStr}));
                              }
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Sözleşme Bitiş
                        </label>
                        <input
                          type="date"
                          value={formData.contract_end_date}
                          onChange={(e) => setFormData({...formData, contract_end_date: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                        {formData.contract_end_date && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {(() => {
                              const today = new Date();
                              const endDate = new Date(formData.contract_end_date);
                              const diffTime = endDate.getTime() - today.getTime();
                              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                              if (diffDays < 0) {
                                return `⚠️ Sözleşme ${Math.abs(diffDays)} gün önce sona erdi`;
                              } else if (diffDays === 0) {
                                return '⚠️ Sözleşme bugün sona eriyor!';
                              } else if (diffDays <= 30) {
                                return `⚠️ Sözleşme ${diffDays} gün sonra sona erecek`;
                              } else if (diffDays <= 90) {
                                return `⏳ Sözleşme ${diffDays} gün sonra sona erecek`;
                              } else {
                                return `✅ Sözleşme ${diffDays} gün sonra sona erecek`;
                              }
                            })()}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Sözleşme Fiyatı (₺)
                        </label>
                        <input
                          type="text"
                          value={formData.contract_price}
                          onChange={(e) => {
                            // Sadece rakam, nokta ve virgül kabul et
                            const value = e.target.value.replace(/[^0-9.,]/g, '');
                            setFormData({...formData, contract_price: value});
                          }}
                          onBlur={(e) => {
                            // Türk para birimi formatına çevir
                            const trimmedValue = e.target.value.trim();
                            if (trimmedValue) {
                              const numValue = parseLocalizedNumber(trimmedValue);
                              if (numValue !== null && numValue > 0) {
                                setFormData({...formData, contract_price: formatLocalizedNumber(numValue)});
                              } else {
                                setFormData({...formData, contract_price: ''});
                              }
                            } else {
                              setFormData({...formData, contract_price: ''});
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="0,00"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Aylık Ödeme (₺)
                        </label>
                        <input
                          type="text"
                          value={formData.monthly_payment}
                          onChange={(e) => {
                            // Sadece rakam, nokta ve virgül kabul et
                            const value = e.target.value.replace(/[^0-9.,]/g, '');
                            setFormData({...formData, monthly_payment: value});
                          }}
                          onBlur={(e) => {
                            // Türk para birimi formatına çevir
                            const trimmedValue = e.target.value.trim();
                            if (trimmedValue) {
                              const numValue = parseLocalizedNumber(trimmedValue);
                              if (numValue !== null && numValue > 0) {
                                setFormData({...formData, monthly_payment: formatLocalizedNumber(numValue)});
                              } else {
                                setFormData({...formData, monthly_payment: ''});
                              }
                            } else {
                              setFormData({...formData, monthly_payment: ''});
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          placeholder="0,00"
                        />
                      </div>
                    </div>
                    </>)}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Atanan Kullanıcı
                      </label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <select
                            value={formData.assigned_user_id}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFormData({...formData, assigned_user_id: value});
                              if (value && value !== '0') {
                                checkUserPhone(value);
                              } else {
                                setUserHasPhone(null);
                                setUserConflictDevice(null);
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          >
                            <option value="">Seçiniz</option>
                            {[...users].sort((a, b) => a.name.localeCompare(b.name, 'tr-TR')).map(user => (
                              <option key={user.id} value={user.id}>
                                {user.name}
                              </option>
                            ))}
                          </select>
                          {checkingUser && (
                            <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                            </div>
                          )}
                        </div>
                        {editingPhone && formData.assigned_user_id && (
                          <button
                            type="button"
                            onClick={() => {
                              if (confirm('Bu kullanıcının atamasını kaldırmak istediğinizden emin misiniz?')) {
                                setFormData({
                                  ...formData,
                                  assigned_user_id: '',
                                  status: 'inactive'
                                });
                                setUserHasPhone(null);
                                setUserConflictDevice(null);
                              }
                            }}
                            className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center gap-1"
                            title="Atamayı Kaldır"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      {userHasPhone === false && (
                        <p className="text-xs text-green-600 mt-1">✓ Kullanıcının başka telefonu yok</p>
                      )}
                      {userHasPhone === true && userConflictDevice && (
                        <p className="text-xs text-orange-600 mt-1">
                          ⚠ Kullanıcının başka bir telefon ataması var: {userConflictDevice.inventory_number} ({userConflictDevice.brand} {userConflictDevice.model})
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Durum
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value="active">Aktif</option>
                        <option value="inactive">Atanmamış</option>
                        <option value="damaged">Hasarlı</option>
                        <option value="lost">Kayıp</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Sahiplik
                      </label>
                      <select
                        value={formData.ownership}
                        onChange={(e) => {
                            const newOwnership = e.target.value;
                            if (newOwnership !== 'rental') {
                              setFormData({...formData, ownership: newOwnership, contract_start_date: '', contract_end_date: '', contract_price: '', monthly_payment: '', agreement_year: ''});
                            } else {
                              setFormData({...formData, ownership: newOwnership});
                            }
                          }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value="">Seçiniz</option>
                        <option value="company">Şirket</option>
                        <option value="rental">Kiralık</option>
                        <option value="employee">Çalışan</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Notlar
                      </label>
                      <textarea
                        rows={4}
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="Ek bilgiler..."
                      />
                    </div>
                  </div>
                </div>

                <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-4 flex justify-end gap-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 sm:flex-none px-4 py-2 min-h-[44px] text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="flex-1 sm:flex-none px-4 py-2 min-h-[44px] bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    {editingPhone ? 'Güncelle' : 'Kaydet'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Modal>


      {/* Quick Assign Modal */}
      <Modal isOpen={showQuickAssignModal} onClose={() => setShowQuickAssignModal(false)}>
          {selectedPhoneForAssign && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Hızlı Kullanıcı Ataması
                </h2>
                <button
                  onClick={() => setShowQuickAssignModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-primary/10 p-3 rounded-lg">
                <div className="flex items-center">
                  <Smartphone className="w-4 h-4 text-primary mr-2" />
                  <span className="text-sm font-medium text-primary-dark">
                    {selectedPhoneForAssign.brand} {selectedPhoneForAssign.model}
                  </span>
                </div>
                <div className="text-xs text-primary mt-1">
                  {selectedPhoneForAssign.inventory_number}
                </div>
                {selectedPhoneForAssign.assigned_user_name && (
                  <div className="mt-2 text-sm text-primary-dark">
                    Mevcut Kullanıcı: {selectedPhoneForAssign.assigned_user_name}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Kullanıcı Ara ve Seç
                </label>
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Kullanıcı adı veya e-posta ara..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent w-full"
                  />
                </div>
                
                <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                  {users.filter(user =>
                    String(user.name || '').toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                    String(user.email || '').toLowerCase().includes(userSearchTerm.toLowerCase())
                  ).length === 0 ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                      {userSearchTerm ? 'Kullanıcı bulunamadı' : 'Kullanıcı listesi yüklüyor...'}
                    </div>
                  ) : (
                    users.filter(user =>
                      String(user.name || '').toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                      String(user.email || '').toLowerCase().includes(userSearchTerm.toLowerCase())
                    ).sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'tr-TR')).map((user) => (
                      <div
                        key={user.id}
                        onClick={() => setSelectedUserId(user.id)}
                        className={`p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 border-b border-gray-100 last:border-b-0 ${
                          selectedUserId === user.id ? 'bg-green-50 border-green-200' : ''
                        }`}
                      >
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-400">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                          </div>
                          <div className="ml-3 flex-1">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {user.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {user.email}
                            </div>
                          </div>
                          {selectedUserId === user.id && (
                            <div className="flex-shrink-0">
                              <div className="w-4 h-4 bg-green-600 rounded-full flex items-center justify-center">
                                <div className="w-2 h-2 bg-white dark:bg-gray-800 rounded-full"></div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-between space-x-3">
              <button
                onClick={() => setShowQuickAssignModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                İptal
              </button>
              <div className="flex space-x-2">
                <button
                  onClick={handleQuickAssignSave}
                  disabled={selectedUserId === 0}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ata
                </button>
                <button
                  onClick={() => {
                    if (selectedUserId === 0) return;
                    const user = users.find(u => u.id === selectedUserId);
                    if (user && selectedPhoneForAssign) {
                      setShowQuickAssignModal(false);
                      handleAssignmentWithForm(selectedPhoneForAssign, selectedUserId.toString(), user.name);
                    }
                  }}
                  disabled={selectedUserId === 0}
                  className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <FileText className="w-4 h-4" />
                  Zimmet Formu ile Ata
                </button>
              </div>
            </div>
          </div>
          )}
        </Modal>

      {/* Zimmet Kaldırma Onay Modal */}
      {/* Arşivleme / arşivden çıkarma onayı */}
      <Modal isOpen={!!archiveDialog} onClose={() => !archiveProcessing && setArchiveDialog(null)}>
        {archiveDialog && (() => {
          const isArchive = archiveDialog.action === 'archive';
          const source = isArchive ? phones : archivedPhones;
          const targets = source.filter(p => archiveDialog.ids.includes(p.id));
          const assignedCount = targets.filter(p => p.assigned_user_id).length;
          const gsmCount = targets.filter(p => p.gsm_number).length;
          return (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  {isArchive ? <Archive className="w-5 h-5 mr-2 text-amber-600" /> : <ArchiveRestore className="w-5 h-5 mr-2 text-green-600" />}
                  {isArchive
                    ? t('mobilePhones.archive.dialogArchiveTitle', { count: targets.length })
                    : t('mobilePhones.archive.dialogRestoreTitle', { count: targets.length })}
                </h2>
                <button
                  onClick={() => setArchiveDialog(null)}
                  disabled={archiveProcessing}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                  aria-label={t('common.close')}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4 text-sm">
              <p className="text-gray-700 dark:text-gray-300">
                {isArchive ? t('mobilePhones.archive.dialogArchiveText') : t('mobilePhones.archive.dialogRestoreText')}
              </p>

              <ul className="max-h-48 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700">
                {targets.map(p => (
                  <li key={p.id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <span className="min-w-0 truncate text-gray-900 dark:text-gray-100">
                      {p.brand} {p.model} <span className="text-gray-500 dark:text-gray-400">· {p.inventory_number}</span>
                    </span>
                    {isArchive && p.assigned_user_name && (
                      <span className="flex-shrink-0 text-xs text-orange-700 dark:text-orange-300">{p.assigned_user_name}</span>
                    )}
                  </li>
                ))}
              </ul>

              {isArchive && assignedCount > 0 && (
                <div className="flex items-start gap-2 rounded-lg bg-orange-50 p-3 text-orange-800 dark:bg-orange-900/20 dark:text-orange-200">
                  <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>{t('mobilePhones.archive.warnAssigned', { count: assignedCount })}</span>
                </div>
              )}
              {isArchive && gsmCount > 0 && (
                <fieldset className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                  <legend className="px-1 font-medium text-gray-700 dark:text-gray-300">
                    {t('mobilePhones.archive.gsmQuestion', { count: gsmCount })}
                  </legend>
                  <label className="flex items-start gap-2 py-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="archive-gsm"
                      checked={!archiveReleaseGsm}
                      onChange={() => setArchiveReleaseGsm(false)}
                      className="mt-0.5 text-primary focus:ring-primary"
                    />
                    <span>
                      <span className="block text-gray-900 dark:text-gray-100">{t('mobilePhones.archive.gsmKeep')}</span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">{t('mobilePhones.archive.gsmKeepHint')}</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 py-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="archive-gsm"
                      checked={archiveReleaseGsm}
                      onChange={() => setArchiveReleaseGsm(true)}
                      className="mt-0.5 text-primary focus:ring-primary"
                    />
                    <span>
                      <span className="block text-gray-900 dark:text-gray-100">{t('mobilePhones.archive.gsmRelease')}</span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">{t('mobilePhones.archive.gsmReleaseHint')}</span>
                    </span>
                  </label>
                </fieldset>
              )}

              {isArchive && (
                <div>
                  <label htmlFor="archive-reason" className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('mobilePhones.archive.reasonLabel')}
                  </label>
                  <input
                    id="archive-reason"
                    type="text"
                    value={archiveReason}
                    onChange={(e) => setArchiveReason(e.target.value)}
                    maxLength={255}
                    placeholder={t('mobilePhones.archive.reasonPlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setArchiveDialog(null)}
                disabled={archiveProcessing}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 min-h-[40px]"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleArchiveConfirm}
                disabled={archiveProcessing || targets.length === 0}
                className={`flex items-center px-4 py-2 rounded-lg text-white font-medium min-h-[40px] disabled:opacity-60 ${
                  isArchive ? 'bg-amber-600 hover:bg-amber-700' : 'bg-primary hover:bg-primary-dark'
                }`}
              >
                {archiveProcessing && <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />}
                {isArchive
                  ? t('mobilePhones.archive.confirmArchive', { count: targets.length })
                  : t('mobilePhones.archive.confirmRestore', { count: targets.length })}
              </button>
            </div>
          </div>
          );
        })()}
      </Modal>

      <Modal isOpen={showUnassignConfirmModal} onClose={() => setShowUnassignConfirmModal(false)}>
          {selectedPhoneForUnassign && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Zimmet Kaldırma Onayı
                </h2>
                <button
                  onClick={() => {
                    setShowUnassignConfirmModal(false);
                    setSelectedPhoneForUnassign(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <Smartphone className="w-5 h-5 text-orange-600 mr-2" />
                  <div>
                    <div className="font-medium text-orange-800">
                      {selectedPhoneForUnassign.brand} {selectedPhoneForUnassign.model}
                    </div>
                    <div className="text-sm text-orange-600">
                      Envanter: {selectedPhoneForUnassign.inventory_number}
                    </div>
                    <div className="text-sm text-orange-600">
                      Atanan: {selectedPhoneForUnassign.assigned_user_name}
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
                <p className="text-gray-700 dark:text-gray-300 mb-2">
                  <strong>{selectedPhoneForUnassign.assigned_user_name}</strong> kullanıcısından
                </p>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  <strong>{selectedPhoneForUnassign.brand} {selectedPhoneForUnassign.model}</strong> cihazının zimmetini kaldırmak istediğinizden emin misiniz?
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowUnassignConfirmModal(false);
                  setSelectedPhoneForUnassign(null);
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleUnassignConfirm}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
              >
                Zimmet Kaldır
              </button>
            </div>
          </div>
          )}
        </Modal>

      {/* Zimmet Geri Teslim Formu Modal */}
      <Modal isOpen={showReturnFormModal} onClose={() => setShowReturnFormModal(false)}>
          {selectedPhoneForUnassign && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Zimmet Geri Teslim Tutanağı
                </h2>
                <button
                  onClick={() => {
                    setShowReturnFormModal(false);
                    setSelectedPhoneForUnassign(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-primary/10 p-4 rounded-lg">
                <div className="flex items-center">
                  <FileText className="w-5 h-5 text-primary mr-2" />
                  <div>
                    <div className="font-medium text-primary-dark">
                      Zimmet Geri Teslim Tutanağı oluşturulsun mu?
                    </div>
                    <div className="text-sm text-primary mt-1">
                      Bu tutanak ile cihazın geri teslim edildiği resmi olarak kayıt altına alınacaktır.
                    </div>
                  </div>
                </div>
              </div>

              {/* Teslim Alınan Ekipmanlar */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Teslim Alınan Ekipmanlar
                </label>
                {returnEquipment.length > 0 && (
                  <div className="mb-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded px-3 py-2">
                    Zimmet sırasında teslim edilen ekipmanlar işaretlendi. Geri aldıklarınızı onaylayın veya güncelleyin.
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  {PHONE_EQUIPMENT_LIST.map((item) => (
                    <label key={item} className="flex items-center gap-2 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={returnEquipment.includes(item)}
                        onChange={(e) => {
                          setReturnEquipment(prev =>
                            e.target.checked ? [...prev, item] : prev.filter(x => x !== item)
                          );
                        }}
                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-primary transition-colors">
                        {item}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Geri Teslim Notları (İsteğe Bağlı)
                </label>
                <textarea
                  value={returnFormNotes}
                  onChange={(e) => setReturnFormNotes(e.target.value)}
                  placeholder="Cihazın durumu, geri teslim sebebi vb. notlar ekleyebilirsiniz..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary focus:border-primary"
                  rows={2}
                />
              </div>

              {/* Cihaz Fotoğrafları */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cihaz Fotoğrafları (En fazla 4 adet)
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {returnFormPhotos.map((photo, idx) => (
                    <div key={idx} className="relative aspect-square">
                      <img src={photo} alt={`Fotoğraf ${idx + 1}`} className="w-full h-full object-cover rounded-md border border-gray-300 dark:border-gray-600" />
                      <button
                        onClick={() => setReturnFormPhotos(prev => prev.filter((_, i) => i !== idx))}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {returnFormPhotos.length < 4 && (
                    <label className="aspect-square border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/10 transition-colors">
                      <Camera className="w-5 h-5 text-gray-400 mb-1" />
                      <span className="text-xs text-gray-400">Ekle</span>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        multiple
                        className="hidden"
                        onChange={addReturnFormPhoto}
                      />
                    </label>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-sm text-gray-600 dark:text-gray-400">
                <div><strong>Teslim Eden:</strong> {selectedPhoneForUnassign.assigned_user_name}</div>
                <div><strong>Teslim Alan:</strong> {JSON.parse(localStorage.getItem('user') || '{}').name}</div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowReturnFormModal(false);
                  setSelectedPhoneForUnassign(null);
                  setReturnFormNotes('');
                  setReturnFormPhotos([]);
                  setReturnEquipment([]);
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={() => handleReturnFormDecision(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                Tutanak Olmadan Kaldır
              </button>
              <button
                onClick={() => handleReturnFormDecision(true)}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors flex items-center gap-1"
              >
                <FileText className="w-4 h-4" />
                Tutanak ile Kaldır
              </button>
            </div>
          </div>
          )}
        </Modal>

      {/* Label Print Modal */}
      <Modal isOpen={showLabelModal} onClose={() => setShowLabelModal(false)}>
          {selectedPhoneForLabel && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-sm">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Cihaz Etiketi
                </h2>
                <button
                  onClick={() => {
                    setShowLabelModal(false);
                    setQrCodeUrl(null);
                    setSelectedPhoneForLabel(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4">
              {/* Phone box sticker preview */}
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-3 bg-white dark:bg-gray-900 flex items-center gap-3" style={{width: '220px', margin: '0 auto'}}>
                <div className="flex-1 min-w-0">
                  <div className="text-base font-bold text-gray-900 dark:text-gray-100 leading-tight">
                    {selectedPhoneForLabel.brand}
                  </div>
                  <div className="text-sm text-gray-700 dark:text-gray-300 leading-tight">
                    {selectedPhoneForLabel.model}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {loadingQrCode ? (
                    <div className="w-16 h-16 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                    </div>
                  ) : qrCodeUrl ? (
                    <img src={qrCodeUrl} alt="QR Code" className="w-16 h-16 rounded" />
                  ) : (
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded flex items-center justify-center text-gray-500 dark:text-gray-400 text-xs text-center">
                      QR yok
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowLabelModal(false);
                  setQrCodeUrl(null);
                  setSelectedPhoneForLabel(null);
             
                }}
                className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={() => {
                  const printWindow = window.open('', '_blank');
                  if (printWindow) {
                    const html = `
                      <!DOCTYPE html>
                      <html>
                      <head>
                        <title>Cihaz Etiketi</title>
                        <style>
                          @page { size: auto; margin: 0; }
                          body { font-family: Arial, sans-serif; margin: 0; padding: 2mm; display: flex; justify-content: center; }
                          .label {
                            border: 1px solid #ccc; border-radius: 4px; padding: 2mm;
                            display: flex; flex-direction: row; align-items: center; gap: 2mm;
                            width: 50mm; height: 25mm; box-sizing: border-box; overflow: hidden;
                          }
                          .label-text { flex: 1; min-width: 0; }
                          .brand-name { font-size: 11px; font-weight: bold; color: #111; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                          .model-name { font-size: 9px; color: #444; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
                          .qr-code { width: 20mm; height: 20mm; flex-shrink: 0; }
                        </style>
                      </head>
                      <body>
                        <div class="label">
                          <div class="label-text">
                            <div class="brand-name">${selectedPhoneForLabel.brand}</div>
                            <div class="model-name">${selectedPhoneForLabel.model}</div>
                          </div>
                          ${qrCodeUrl ? '<img class="qr-code" src="' + qrCodeUrl + '" alt="QR" />' : '<div class="qr-code" style="font-size:8px; display:flex; align-items:center; justify-content:center; border:1px solid #ccc;">QR Yok</div>'}
                        </div>
                        <script>
                          window.onload = function() { window.print(); window.setTimeout(function(){ window.close(); }, 500); };
                        </script>
                      </body>
                      </html>
                    `;
                    printWindow.document.write(html);
                    printWindow.document.close();
                  }
                  setShowLabelModal(false);
                }}
                className="px-3 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center"
              >
                <Printer className="w-4 h-4 mr-1" />
                Yazdır
              </button>
            </div>
          </div>
          )}
        </Modal>

      {/* Bulk Import Modal */}
      <Modal isOpen={showBulkImportModal} onClose={() => setShowBulkImportModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[95vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Excel'den Cep Telefonu İçeri Aktar
                </h2>
                <button
                  onClick={() => {
                    setShowBulkImportModal(false);
                    resetImportModal();
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Instructions */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Kullanım Talimatları:
                </h3>
                <div className="bg-primary/10 p-4 rounded-lg">
                  <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700 dark:text-gray-300">
                    <li>Excel'de cep telefonu verilerinizi aşağıdaki sütun adlarıyla hazırlayın (isterseniz <strong>Şablon İndir</strong> ile başlayın)</li>
                    <li>Verileri başlık satırıyla birlikte kopyalayıp aşağıdaki alana yapıştırın (Ctrl+C / Ctrl+V)</li>
                    <li>veya Excel dosyasını en alttaki <strong>Dosya Yükleme</strong> bölümünden yükleyin</li>
                  </ol>
                </div>
              </div>

              {/* Tanınan sütun adları */}
              <div className="mb-6">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Excel Sütun Adları
                  </h3>
                  <button
                    type="button"
                    onClick={downloadImportTemplate}
                    className="inline-flex items-center gap-2 px-3 py-2 min-h-[44px] text-sm font-medium text-primary border border-primary/40 rounded-lg hover:bg-primary/10 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Şablon İndir
                  </button>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg text-sm">
                  <ul className="space-y-1 text-gray-700 dark:text-gray-300 mb-4">
                    <li>• İlk satır başlık satırı olmalı; sütun sırası önemli değil, kullanmadığınız sütunları eklemeyin.</li>
                    <li>• Başlığı aşağıdaki adlardan biriyle yazın. Büyük/küçük harf fark etmez; boşluk yerine <code className="px-1 rounded bg-white dark:bg-gray-800">_</code> veya <code className="px-1 rounded bg-white dark:bg-gray-800">-</code> kullanabilirsiniz (ör. <code className="px-1 rounded bg-white dark:bg-gray-800">Abonelik No</code> = <code className="px-1 rounded bg-white dark:bg-gray-800">abonelik_no</code>).</li>
                    <li>• Sistemden <strong>Excel</strong> ile dışa aktardığınız dosyanın başlıkları olduğu gibi tanınır; dosyayı düzenleyip geri yükleyebilirsiniz.</li>
                    <li>• Kendi dosyanızda Envanter No, IMEI, Seri No, Abonelik No ve GSM No sütunlarını Excel'de <strong>Metin</strong> biçimine alın; yoksa baştaki sıfırlar silinir, uzun numaralar bozulur. <strong>Şablon İndir</strong> ile gelen dosyada bu ayar hazırdır.</li>
                    <li>• Envanter No'su sistemde olan satırlar yeni kayıt açmaz, mevcut kaydı günceller: dolu hücreler karşılaştırılır, farklar onayınıza sunulur. Boş hücre mevcut değeri silmez.</li>
                  </ul>
                  {importHeaderAliases ? (
                    <div className="divide-y divide-gray-200 dark:divide-gray-600 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800">
                      {IMPORT_FIELDS.filter(f => importHeaderAliases[f.field]).map(f => (
                        <div key={f.field} className="px-3 py-2 sm:flex sm:items-start sm:gap-4">
                          <div className="sm:w-44 flex-shrink-0 font-medium text-gray-900 dark:text-gray-100">
                            {f.label}
                            {f.required === 'always' && (
                              <span className="ml-2 text-xs font-normal text-red-600 dark:text-red-400">zorunlu</span>
                            )}
                            {f.required === 'new' && (
                              <span className="ml-2 text-xs font-normal text-amber-600 dark:text-amber-400">yeni kayıtta zorunlu</span>
                            )}
                          </div>
                          <div className="mt-1 sm:mt-0 min-w-0">
                            {f.hint && (
                              <p className="mb-1 text-xs text-gray-600 dark:text-gray-400">{f.hint}</p>
                            )}
                            <div className="flex flex-wrap gap-1">
                            {importHeaderAliases[f.field].map(alias => (
                              <code key={alias} className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-xs text-gray-700 dark:text-gray-300">
                                {alias}
                              </code>
                            ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400">Sütun adları yükleniyor...</p>
                  )}
                </div>
              </div>

              {/* Copy-Paste Area */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Excel Verilerini Yapıştırın:
                </label>
                <textarea
                  value={importData}
                  onChange={(e) => handleDataChange(e.target.value)}
                  placeholder="Excel'den kopyaladığınız verileri buraya yapıştırın..."
                  className="w-full h-64 p-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm"
                />
              </div>

              {/* Preview Section */}
              {importData.trim() && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    Veri Önizlemesi:
                  </h3>

                  {/* Column Mapping Info */}
                  {Object.keys(columnMapping).length > 0 && (
                    <div className="bg-primary/10 p-4 rounded-lg mb-4">
                      <p className="text-primary-dark font-semibold mb-2">
                        📋 Sütun Eşleştirmesi:
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        {Object.entries(columnMapping).map(([field, column]) => {
                          const fieldNames: {[key: string]: string} = {
                            inventory_number: 'Envanter No',
                            creation_date: 'Oluşturma Tarihi',
                            exchange_rate: 'Döviz Kuru',
                            agreement_year: 'Anlaşma Yılı',
                            currency: 'Para Birimi',
                            brand: 'Marka',
                            model: 'Model',
                            color: 'Renk',
                            storage: 'Hafıza',
                            imei1: 'IMEI 1',
                            imei2: 'IMEI 2',
                            serial_number: 'Seri No',
                            subscription_number: 'Abonelik No',
                            gsm_number: 'GSM No',
                            location: 'Lokasyon',
                            company: 'Şirket',
                            supplier: 'Tedarikçi',
                            notes: 'Notlar',
                            ownership: 'Sahiplik',
                            status: 'Durum',
                            assigned_person: 'Atanan Kişi',
                            contract_start_date: 'Sözleşme Başlangıç',
                            contract_end_date: 'Sözleşme Bitiş',
                            contract_price: 'Toplam Fiyat',
                            monthly_payment: 'Aylık Ödeme'
                          };
                          return (
                            <div key={field} className="flex items-center justify-between bg-white dark:bg-gray-800 px-3 py-2 rounded">
                              <span className="text-gray-700 dark:text-gray-300 font-medium">{fieldNames[field]}:</span>
                              <span className="text-gray-900 dark:text-gray-100">{column}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Parse Errors */}
                  {parseErrors.length > 0 && (
                    <div className="bg-red-50 p-4 rounded-lg mb-4">
                      <p className="text-red-800 font-semibold mb-2">
                        ❌ Parse Hataları ({parseErrors.length} hata):
                      </p>
                      <div className="max-h-60 overflow-y-auto">
                        <ul className="list-disc list-inside space-y-1 text-sm text-red-700">
                          {parseErrors.slice(0, 20).map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                          {parseErrors.length > 20 && (
                            <li className="text-red-600 font-medium">
                              ... ve {parseErrors.length - 20} hata daha
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Success Preview */}
                  {parsedData.length > 0 && (
                    <div className="bg-green-50 p-4 rounded-lg mb-4">
                      <p className="text-green-800 font-semibold mb-2">
                        ✅ {parsedData.length} satır okundu (henüz içe aktarılmadı)
                      </p>
                      <div className="text-green-700 text-sm space-y-1">
                        <p>📊 Toplam satır: {parseStats.totalRows}</p>
                        <p>✅ Okunan: {parsedData.length}
                          {' '}({parsedData.filter(p => existingInventoryNumbers.has(String(p.inventory_number || '').trim())).length} mevcut kaydı güncelleyecek,
                          {' '}{parsedData.filter(p => !existingInventoryNumbers.has(String(p.inventory_number || '').trim())).length} yeni kayıt)</p>
                        <p>❌ Okunamayan: {parseStats.errorRows}</p>
                        {parseErrors.length > 0 && (
                          <p className="text-orange-600">
                            ⚠️ {parseErrors.length} satırda hata var, sadece geçerli satırlar import edilecek
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Data Preview Table: eşleşen tüm sütunlar, her satırın mevcut/yeni kayıt durumu */}
                  {parsedData.length > 0 && (() => {
                    const previewFields = IMPORT_FIELDS.filter(f => columnMapping[f.field]);
                    return (
                    <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                      <table className="min-w-full bg-white dark:bg-gray-800">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">Satır</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">İşlem</th>
                            {previewFields.map(f => (
                              <th key={f.field} className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                {f.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {parsedData.slice(0, 20).map((phone, index) => {
                            const exists = existingInventoryNumbers.has(String(phone.inventory_number || '').trim());
                            return (
                              <tr key={index}>
                                <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{phone.rowNumber}</td>
                                <td className="px-3 py-2 text-xs whitespace-nowrap">
                                  {exists ? (
                                    <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">Mevcut kayıt · güncellenecek</span>
                                  ) : (
                                    <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200">Yeni kayıt</span>
                                  )}
                                </td>
                                {previewFields.map(f => {
                                  const value = phone[f.field];
                                  const empty = value === undefined || value === null || String(value).trim() === '';
                                  return (
                                    <td key={f.field} className={`px-3 py-2 text-sm whitespace-nowrap ${f.field === 'inventory_number' ? 'font-medium' : ''} ${empty ? 'text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
                                      {empty ? '—' : String(value)}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {parsedData.length > 20 && (
                        <p className="text-center text-gray-500 dark:text-gray-400 text-sm py-2">
                          ... ve {parsedData.length - 20} satır daha
                        </p>
                      )}
                      <p className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
                        "—" boş hücredir; mevcut kayıtta o alanın değeri değişmez. Mevcut kayıtlardaki farklar içe aktarmadan önce ayrıca onayınıza sunulur.
                      </p>
                    </div>
                    );
                  })()}
                </div>
              )}

              {/* Alternative: File Upload */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Alternatif: Dosya Yükleme
                </h3>
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6">
                  <div className="text-center">
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      Excel dosyasını sürükleyin veya seçin
                    </div>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="bulk-import-file"
                    />
                    <label
                      htmlFor="bulk-import-file"
                      className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 dark:bg-gray-600 transition-colors"
                    >
                      Dosya Seç
                    </label>
                  </div>

                  {uploadedFile && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                      <div className="flex items-center">
                        <FileText className="w-4 h-4 text-green-600 mr-2" />
                        <span className="text-sm text-green-700 font-medium">
                          {uploadedFile.name}
                        </span>
                        <button
                          onClick={() => setUploadedFile(null)}
                          className="ml-auto text-green-600 hover:text-green-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Error/Success Messages */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 text-sm">{success}</p>
                </div>
              )}

              {/* Import Results */}
              {importResults && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="text-center">
                        <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                        <div className="text-lg font-bold text-green-800">
                          {importResults.successful}
                        </div>
                        <div className="text-sm text-green-600">{t('mobilePhones.importCreated')}</div>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="text-center">
                        <Edit className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                        <div className="text-lg font-bold text-blue-800">
                          {importResults.updated || 0}
                        </div>
                        <div className="text-sm text-blue-600">{t('mobilePhones.importUpdated')}</div>
                      </div>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="text-center">
                        <Clock className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                        <div className="text-lg font-bold text-gray-800">
                          {(importResults.unchanged || 0) + (importResults.skipped || 0)}
                        </div>
                        <div className="text-sm text-gray-600">{t('mobilePhones.importUnchangedSkipped')}</div>
                      </div>
                    </div>

                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="text-center">
                        <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                        <div className="text-lg font-bold text-red-800">
                          {importResults.failed}
                        </div>
                        <div className="text-sm text-red-600">{t('mobilePhones.importFailed')}</div>
                      </div>
                    </div>
                  </div>

                  {importResults.rows && importResults.rows.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Satır Detayları:</h4>
                      <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
                        {[...importResults.rows].sort((x: any, y: any) => x.row - y.row).map((r: any, index: number) => {
                          const fieldNames = (r.fields || []).map((f: string) => t(`notif.change.field.${f}`)).join(', ');
                          const badge = {
                            created: ['Eklendi', 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'],
                            updated: ['Güncellendi', 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'],
                            unchanged: ['Değişmedi', 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'],
                            skipped: ['Atlandı', 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200']
                          }[r.result as string] || [r.result, 'bg-gray-100 text-gray-700'];
                          return (
                            <div key={index} className="px-3 py-2 text-sm flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3">
                              <div className="flex items-center gap-2 sm:w-56 flex-shrink-0">
                                <span className="text-gray-500 dark:text-gray-400">Satır {r.row}</span>
                                <span className="font-medium text-gray-900 dark:text-gray-100">{r.inventory_number}</span>
                                <span className={`px-2 py-0.5 rounded-full text-xs ${badge[1]}`}>{badge[0]}</span>
                              </div>
                              <div className="text-gray-600 dark:text-gray-400 min-w-0">
                                {r.result === 'updated' && <>Değişen alanlar: <span className="text-gray-900 dark:text-gray-100">{fieldNames}</span></>}
                                {r.result === 'unchanged' && (fieldNames
                                  ? <>Excel'deki değerler mevcut kayıtla aynı: <span className="text-gray-900 dark:text-gray-100">{fieldNames}</span></>
                                  : <>Excel satırında güncellenecek dolu alan yok</>)}
                                {r.result === 'skipped' && <>Farklar onaylanmadığı için güncellenmedi</>}
                                {r.result === 'created' && <>Yeni kayıt olarak eklendi</>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {importResults.errors && importResults.errors.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Hatalar:</h4>
                      <div className="max-h-32 overflow-y-auto bg-red-50 border border-red-200 rounded-lg p-3">
                        {importResults.errors.map((error: any, index: number) => (
                          <div key={index} className="text-sm text-red-700 mb-1">
                            Satır {error.row}: {error.message}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowBulkImportModal(false);
                  resetImportModal();
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                Kapat
              </button>

              {/* Copy-Paste Import Button */}
              {importData.trim() && parsedData.length > 0 && (
                <button
                  onClick={handleCopyPasteImport}
                  disabled={importing}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {importing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      İçeri Aktarılıyor...
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Yapıştırılan Veriyi İçeri Aktar
                    </>
                  )}
                </button>
              )}

              {/* File Upload Import Button */}
              {uploadedFile && (
                <button
                  onClick={handleBulkImport}
                  disabled={importing}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {importing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      İçeri Aktarılıyor...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Dosyayı İçeri Aktar
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </Modal>

      {/* Toplu içe aktarma: mevcut Envanter No'lu kayıtlardaki farkların onayı */}
      <Modal isOpen={!!importPreview} onClose={() => setImportPreview(null)} zIndex="z-[70]">
        {importPreview && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-start gap-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {t('mobilePhones.importPreviewTitle')}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t('mobilePhones.importPreviewDesc', { count: importPreview.data.updates.length })}
                </p>
              </div>
              <button onClick={() => setImportPreview(null)} className="text-gray-400 hover:text-gray-600 dark:text-gray-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
              <label className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={selectedUpdateIds.size === importPreview.data.updates.length}
                  onChange={(e) => setSelectedUpdateIds(e.target.checked ? new Set(importPreview.data.updates.map(u => u.id)) : new Set())}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                {t('mobilePhones.importSelectAll')}
              </label>
              <span className="text-gray-500 dark:text-gray-400">
                {t('mobilePhones.importPreviewStats', {
                  create: importPreview.data.toCreate,
                  unchanged: importPreview.data.unchanged,
                  failed: importPreview.data.failed
                })}
              </span>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-3">
              {importPreview.data.updates.map(update => (
                <label
                  key={update.id}
                  className={`block border rounded-lg p-3 cursor-pointer transition-colors ${
                    selectedUpdateIds.has(update.id)
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 dark:border-gray-700 opacity-70'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <input
                      type="checkbox"
                      checked={selectedUpdateIds.has(update.id)}
                      onChange={() => toggleUpdateSelection(update.id)}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{update.inventory_number}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">{update.device}</span>
                    {update.is_archived && (
                      <span className="text-xs px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                        {t('mobilePhones.importArchivedBadge')}
                      </span>
                    )}
                    <span className="ml-auto text-xs text-gray-400">{t('mobilePhones.importRow', { row: update.row })}</span>
                  </div>
                  <NotificationChangeList
                    changes={formatFieldChanges(update.changes, t, language)}
                    t={t}
                    className="text-sm pl-7"
                  />
                </label>
              ))}

              {importPreview.data.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-red-800 mb-1">{t('mobilePhones.importPreviewErrors')}</h4>
                  <div className="max-h-32 overflow-y-auto">
                    {importPreview.data.errors.map((err, index) => (
                      <div key={index} className="text-sm text-red-700">
                        {t('mobilePhones.importRow', { row: err.row })}: {err.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setImportPreview(null)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {t('mobilePhones.importCancel')}
              </button>
              <button
                onClick={confirmImportPreview}
                disabled={selectedUpdateIds.size === 0 && importPreview.data.toCreate === 0}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('mobilePhones.importConfirm', { updated: selectedUpdateIds.size, created: importPreview.data.toCreate })}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Phone History Modal */}
      <Modal isOpen={showHistoryModal} onClose={() => setShowHistoryModal(false)}>
          {selectedPhoneForHistory && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  İşlem Geçmişi - {selectedPhoneForHistory.brand} {selectedPhoneForHistory.model}
                </h2>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                <p><strong>Envanter No:</strong> {selectedPhoneForHistory.inventory_number}</p>
                <p><strong>IMEI:</strong> {selectedPhoneForHistory.imei1}</p>
                <p><strong>Mevcut Kullanıcı:</strong> {selectedPhoneForHistory.assigned_user_name || 'Atanmamış'}</p>
              </div>
            </div>
            
            <div className="p-6">
              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">İşlem geçmişi yükleniyor...</span>
                </div>
              ) : phoneHistory.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Bu cihaz için işlem geçmişi bulunamadı.
                </div>
              ) : (
                <div className="space-y-4">
                  {phoneHistory.map((record, index) => (
                    <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              record.type === 'service_record' ? 'bg-yellow-100 text-yellow-800' :
                              record.action === 'create' ? 'bg-green-100 text-green-800' :
                              record.action === 'update' ? 'bg-primary/20 text-primary-dark' :
                              record.action === 'quick_assign' ? 'bg-purple-100 text-purple-800' :
                              record.action === 'delete' ? 'bg-red-100 text-red-800' :
                              record.action === 'archive' ? 'bg-amber-100 text-amber-800' :
                              record.action === 'unarchive' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                            }`}>
                              {(() => {
                                const actionTranslations = {
                                  'create': 'Oluşturma',
                                  'update': 'Güncelleme',
                                  'quick_assign': 'Hızlı Atama',
                                  'unassign': 'Zimmet Kaldırma',
                                  'delete': 'Silme',
                                  'archive': 'Arşivleme',
                                  'unarchive': 'Arşivden Çıkarma',
                                  'service_record': 'Servis Kaydı'
                                };
                                return actionTranslations[record.action as keyof typeof actionTranslations] || record.action_text || record.action;
                              })()}
                            </span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {(() => {
                                const dateField = record.changed_at || record.created_at || record.date;
                                if (!dateField) return 'Tarih bilinmiyor';
                                try {
                                  return new Date(dateField).toLocaleString('tr-TR', {
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  });
                                } catch (error) {
                                  return 'Geçersiz tarih';
                                }
                              })()}
                            </span>
                          </div>

                          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            <strong>İşlemi Yapan:</strong> {record.changed_by_name || record.created_by_name || 'Bilinmeyen'}
                          </div>

                          {/* Service Record Details */}
                          {record.type === 'service_record' && (
                            <div className="text-sm space-y-2 bg-yellow-50 p-3 rounded">
                              <div><strong>Servis No:</strong> {record.service_number}</div>
                              <div><strong>Arıza Açıklaması:</strong> {record.fault_description}</div>
                              {record.service_description && (
                                <div><strong>Servis Açıklaması:</strong> {record.service_description}</div>
                              )}
                              {record.technician_name && (
                                <div><strong>Teknisyen:</strong> {record.technician_name}</div>
                              )}
                              {record.supplier_name && (
                                <div><strong>Tedarikçi:</strong> {record.supplier_name}</div>
                              )}
                              {record.cost && (
                                <div><strong>Maliyet:</strong> {record.cost} TL</div>
                              )}
                              <div><strong>Durum:</strong> {record.status === 'pending' ? 'Beklemede' :
                                                          record.status === 'in_progress' ? 'İşlemde' :
                                                          record.status === 'completed' ? 'Tamamlandı' :
                                                          record.status === 'cancelled' ? 'İptal Edildi' : record.status}</div>
                              {record.notes && (
                                <div><strong>Notlar:</strong> {record.notes}</div>
                              )}
                            </div>
                          )}

                          {/* Mobile Action Details */}
                          {record.type === 'mobile_action' && (
                            <div className="text-sm">
                              {record.details && (
                                <div className="mb-2">
                                  <strong>Detay:</strong> {record.details}
                                </div>
                              )}

                              {(record.action === 'quick_assign' || record.action === 'unassign') && record.new_data && (
                                <div className="space-y-1 bg-purple-50 p-3 rounded">
                                  {(() => {
                                    try {
                                      const newData = JSON.parse(record.new_data) || {};
                                      return (
                                        <div>
                                          <strong>{record.action === 'unassign' ? 'Zimmet Kaldırma Detayı:' : 'Atama Detayı:'}</strong>
                                          <div className="ml-4 mt-1">
                                            {record.action === 'unassign' ? (
                                              <p>"{newData.old_assigned_user_name}" kullanıcısından zimmet kaldırıldı</p>
                                            ) : newData.old_assigned_user_name ? (
                                              <p>"{newData.old_assigned_user_name}" kullanıcısından "{newData.new_assigned_user_name}" kullanıcısına atandı</p>
                                            ) : (
                                              <p>"{newData.new_assigned_user_name}" kullanıcısına atandı</p>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    } catch (e) {
                                      return <p>Atama işlemi gerçekleştirildi</p>;
                                    }
                                  })()}
                                </div>
                              )}

                              {(record.old_data || record.new_data) && record.action !== 'quick_assign' && record.action !== 'unassign' && record.action !== 'service_record' && (
                                <div className="space-y-1 mt-2">
                                  <strong>Değişiklik Detayları:</strong>
                                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded text-sm">
                                    {(() => {
                                      try {
                                        const oldData = record.old_data ? (JSON.parse(record.old_data) || {}) : {};
                                        const newData = record.new_data ? (JSON.parse(record.new_data) || {}) : {};
                                        const changes = [];

                                        // Field name translations
                                        const fieldTranslations = {
                                          'assigned_user_id': 'Atanan Kullanıcı',
                                          'assigned_user_name': 'Atanan Kullanıcı',
                                          'inventory_number': 'Envanter No',
                                          'brand': 'Marka',
                                          'model': 'Model',
                                          'imei1': 'IMEI 1',
                                          'imei2': 'IMEI 2',
                                          'serial_number': 'Seri No',
                                          'subscription_number': 'Abonelik No',
                                          'color': 'Renk',
                                          'storage': 'Depolama',
                                          'monthly_payment': 'Aylık Ödeme',
                                          'contract_price': 'Sözleşme Tutarı',
                                          'contract_start_date': 'Sözleşme Başlangıç',
                                          'contract_end_date': 'Sözleşme Bitiş',
                                          'location': 'Lokasyon',
                                          'supplier': 'Tedarikçi',
                                          'ownership': 'Sahiplik',
                                          'status': 'Durum',
                                          'notes': 'Notlar',
                                          'gsm_number': 'GSM No',
                                          'archived': 'Arşivde',
                                          'archive_reason': 'Arşiv Nedeni'
                                        };

                                        // Combine all fields to check
                                        const allFields = new Set([...Object.keys(oldData), ...Object.keys(newData)]);

                                        for (const key of allFields) {
                                          const oldValue = oldData[key];
                                          const newValue = newData[key];

                                          if (oldValue !== newValue) {
                                            const fieldName = fieldTranslations[key] || key;
                                            
                                            // assigned_user_id için kullanıcı adını kullan
                                            let oldDisplay = oldValue ? String(oldValue) : 'Boş';
                                            let newDisplay = newValue ? String(newValue) : 'Boş';
                                            
                                            if (key === 'assigned_user_id') {
                                              // Önce assigned_user_name'i kontrol et
                                              const oldUserName = oldData.assigned_user_name;
                                              const newUserName = newData.assigned_user_name;
                                              
                                              if (oldUserName) {
                                                oldDisplay = oldUserName;
                                              } else if (oldValue && users.length > 0) {
                                                const oldUser = users.find(u => u.id === parseInt(String(oldValue)));
                                                oldDisplay = oldUser ? oldUser.name : String(oldValue);
                                              }
                                              
                                              if (newUserName) {
                                                newDisplay = newUserName;
                                              } else if (newValue && users.length > 0) {
                                                const newUser = users.find(u => u.id === parseInt(String(newValue)));
                                                newDisplay = newUser ? newUser.name : String(newValue);
                                              }
                                            }
                                            
                                            changes.push(
                                              <div key={key} className="flex flex-col sm:flex-row gap-1">
                                                <span className="font-medium text-gray-700 dark:text-gray-300">{fieldName}:</span>
                                                <span className="text-red-600">"{oldDisplay}"</span>
                                                <span className="text-gray-500 dark:text-gray-400">→</span>
                                                <span className="text-green-600">"{newDisplay}"</span>
                                              </div>
                                            );
                                          }
                                        }

                                        return changes.length > 0 ? (
                                          <div className="space-y-2">{changes}</div>
                                        ) : (
                                          <p className="text-gray-500 dark:text-gray-400">Değişiklik detayı bulunamadı</p>
                                        );
                                      } catch (e) {
                                        console.error('Error parsing change data:', e);
                                        return (
                                          <p className="text-gray-500 dark:text-gray-400">
                                            {record.details || 'Değişiklik detayı görüntülenemedi'}
                                          </p>
                                        );
                                      }
                                    })()}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
          )}
        </Modal>

      {/* Zimmet Formu Onay Modal */}
      <Modal isOpen={showAssignmentFormModal} onClose={() => setShowAssignmentFormModal(false)}>
          {pendingAssignment && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Zimmet Teslim Formu Oluştur
              </h3>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  <strong>{pendingAssignment.phone?.inventory_number}</strong> envanter numaralı 
                  <strong> {pendingAssignment.phone?.brand} {pendingAssignment.phone?.model}</strong> cep telefonu 
                  <strong> {pendingAssignment.userName}</strong> kullanıcısına atanacak.
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                  Bu atama için zimmet teslim formu oluşturmak istiyor musunuz?
                </p>
                
                {/* Teslim Edilen Ekipmanlar */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Teslim Edilen Ekipmanlar
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {PHONE_EQUIPMENT_LIST.map((item) => (
                      <label key={item} className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={assignmentEquipment.includes(item)}
                          onChange={(e) => {
                            setAssignmentEquipment(prev =>
                              e.target.checked ? [...prev, item] : prev.filter(x => x !== item)
                            );
                          }}
                          className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-primary transition-colors">
                          {item}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notlar (Opsiyonel)
                </label>
                <textarea
                  value={assignmentFormNotes}
                  onChange={(e) => setAssignmentFormNotes(e.target.value)}
                  placeholder="Özel durumlar vb."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  rows={2}
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-col space-y-3">
                {/* Ana Aksiyon Butonları */}
                <div className="flex space-x-3">
                  <button
                    onClick={confirmAssignmentWithForm}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    Zimmet Formu ile Ata
                  </button>
                  <button
                    onClick={confirmAssignmentWithoutForm}
                    className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors flex items-center justify-center gap-2"
                  >
                    <User className="w-4 h-4" />
                    Form Olmadan Ata
                  </button>
                </div>
                
                {/* İptal Butonu */}
                <button
                  onClick={() => {
                    setShowAssignmentFormModal(false);
                    setPendingAssignment(null);
                    setAssignmentFormNotes('');
                    setAssignmentEquipment([]);
                  }}
                  className="w-full px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                >
                  İptal
                </button>
              </div>
            </div>
          </div>
          )}
        </Modal>

      {/* Bulk Label Modal */}
      <Modal isOpen={showBulkLabelModal} onClose={() => setShowBulkLabelModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <QrCode className="h-6 w-6" />
                Toplu Etiket Yazdırma
              </h2>
              <button
                onClick={() => {
                  setShowBulkLabelModal(false);
                  setSelectedPhones([]);
                  setShowBulkLabelPreview(false);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {!showBulkLabelPreview ? (
                <>
                  {/* Filters */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                      Filtreler
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Lokasyon
                        </label>
                        <select
                          value={bulkLabelFilters.location}
                          onChange={(e) => setBulkLabelFilters({...bulkLabelFilters, location: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="all">Tüm Lokasyonlar</option>
                          {[...new Set(phones.map(p => p.location_name).filter(Boolean))].map(location => (
                            <option key={location} value={location}>{location}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Durum
                        </label>
                        <select
                          value={bulkLabelFilters.status}
                          onChange={(e) => setBulkLabelFilters({...bulkLabelFilters, status: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="all">Tüm Durumlar</option>
                          <option value="active">Aktif</option>
                          <option value="inactive">Pasif</option>
                          <option value="damaged">Hasarlı</option>
                          <option value="lost">Kayıp</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Marka
                        </label>
                        <select
                          value={bulkLabelFilters.brand}
                          onChange={(e) => setBulkLabelFilters({...bulkLabelFilters, brand: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="all">Tüm Markalar</option>
                          {[...new Set(phones.map(p => p.brand).filter(Boolean))].map(brand => (
                            <option key={brand} value={brand}>{brand}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="mt-4">
                      <button
                        onClick={applyBulkLabelFilters}
                        className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center gap-2"
                      >
                        <Search className="h-4 w-4" />
                        Filtrele
                      </button>
                    </div>
                  </div>

                  {/* Phone Selection */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Telefon Seçimi ({selectedPhones.length} seçili)
                      </h3>
                      <div className="flex gap-2">
                        <button
                          onClick={selectAllFiltered}
                          className="text-sm bg-primary text-white px-3 py-1 rounded hover:bg-primary-dark"
                        >
                          Tümünü Seç
                        </button>
                        <button
                          onClick={deselectAll}
                          className="text-sm bg-gray-600 text-white px-3 py-1 rounded hover:bg-gray-700"
                        >
                          Seçimi Temizle
                        </button>
                      </div>
                    </div>

                    <div className="max-h-64 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg">
                      <div className="grid grid-cols-1 gap-2 p-4">
                        {(filteredPhonesForLabel.length > 0 ? filteredPhonesForLabel : phones).map(phone => (
                          <label
                            key={phone.id}
                            className="flex items-center space-x-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 rounded-lg cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedPhones.includes(phone.id)}
                              onChange={() => handlePhoneSelection(phone.id)}
                              className="rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 dark:text-gray-100">
                                {phone.brand} {phone.model}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {phone.inventory_number} - {phone.location_name || 'Lokasyon yok'} - {phone.status}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setShowBulkLabelModal(false);
                        setSelectedPhones([]);
                      }}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300"
                    >
                      İptal
                    </button>
                    <button
                      onClick={generateBulkLabelPreview}
                      disabled={selectedPhones.length === 0}
                      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      Önizleme ({selectedPhones.length})
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Preview */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                      Etiket Önizlemesi ({selectedPhones.length} adet - {Math.ceil(selectedPhones.length / 4)} sayfa)
                    </h3>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Her sayfada 4 etiket (2x2) basılacak. A4 kağıt boyutunda optimize edilmiştir.
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowBulkLabelPreview(false)}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300"
                    >
                      Geri
                    </button>
                    <button
                      onClick={printBulkLabels}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                    >
                      <Printer className="h-4 w-4" />
                      Yazdır
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </Modal>
    </div>
  );
};

export default MobilePhonesPage;